import React, { useState, useEffect, useRef } from 'react';
import { Building2, Users, Calendar, Phone, BarChart3, ClipboardCheck, Stethoscope, TrendingUp, AlertCircle, CheckCircle, ArrowRight, ExternalLink, User, MessageSquare, Bell, Home, Plus, Clock, Check, Activity, Brain, Heart, Edit3, Trash2, UserCog, Briefcase, Shield, Headphones, Monitor, Clipboard, Settings, Star, Award, Target, Zap, Eye, BookOpen, GraduationCap, Pill, Hospital, Bed, UserPlus, UserCheck, Users2, Square, RefreshCw } from 'lucide-react';
import logo from './logo.png';
import { SHARED_PERSONAS, SHARED_COMMENTS, SHARED_JOURNEY_STAGES, SHARED_JOURNEY_BUNDLES } from './data.js';
import SupervisieDashboard from './components/SupervisieDashboard';
import SalarisverwerkingDashboard from './components/SalarisverwerkingDashboard';
import EpicOverview from './components/EpicOverview';

// Available icons for persona selector
const PERSONA_ICONS = [
  { id: 'user', name: 'Persoon', icon: User },
  { id: 'users', name: 'Team', icon: Users },
  { id: 'users2', name: 'Groep', icon: Users2 },
  { id: 'userCog', name: 'Beheerder', icon: UserCog },
  { id: 'userPlus', name: 'Nieuwe gebruiker', icon: UserPlus },
  { id: 'userCheck', name: 'Geverifieerd', icon: UserCheck },
  { id: 'building2', name: 'Gebouw', icon: Building2 },
  { id: 'briefcase', name: 'Zakelijk', icon: Briefcase },
  { id: 'stethoscope', name: 'Arts', icon: Stethoscope },
  { id: 'heart', name: 'Zorg', icon: Heart },
  { id: 'activity', name: 'Activiteit', icon: Activity },
  { id: 'brain', name: 'Specialist', icon: Brain },
  { id: 'pill', name: 'Medicatie', icon: Pill },
  { id: 'clipboard', name: 'Administratie', icon: Clipboard },
  { id: 'clipboardCheck', name: 'Checklist', icon: ClipboardCheck },
  { id: 'phone', name: 'Telefoon', icon: Phone },
  { id: 'headphones', name: 'Support', icon: Headphones },
  { id: 'monitor', name: 'Scherm', icon: Monitor },
  { id: 'calendar', name: 'Planning', icon: Calendar },
  { id: 'clock', name: 'Tijd', icon: Clock },
  { id: 'barChart3', name: 'Statistieken', icon: BarChart3 },
  { id: 'trendingUp', name: 'Groei', icon: TrendingUp },
  { id: 'target', name: 'Doel', icon: Target },
  { id: 'shield', name: 'Veiligheid', icon: Shield },
  { id: 'settings', name: 'Instellingen', icon: Settings },
  { id: 'star', name: 'Ster', icon: Star },
  { id: 'award', name: 'Award', icon: Award },
  { id: 'zap', name: 'Snel', icon: Zap },
  { id: 'eye', name: 'Toezicht', icon: Eye },
  { id: 'bookOpen', name: 'Kennis', icon: BookOpen },
  { id: 'graduationCap', name: 'Opleiding', icon: GraduationCap },
  { id: 'hospital', name: 'Ziekenhuis', icon: Hospital },
  { id: 'bed', name: 'Bed', icon: Bed },
];

// Roadmap status configuration
const ROADMAP_STATUSES = [
  { id: 'draft', label: 'Draft', color: '#F1C40F', bgColor: '#FEF9E7', icon: '💡' },
  { id: 'ready', label: 'Ready for Dev', color: '#3498DB', bgColor: '#EBF5FB', icon: '📋' },
  { id: 'development', label: 'Under Development', color: '#27AE60', bgColor: '#E8F8F0', icon: '🚀' },
  { id: 'done', label: 'Done', color: '#9B59B6', bgColor: '#F5EEF8', icon: '🎉' }
];

const QUARTERS = [
  { id: 'Q1-2026', label: 'Q1 2026', months: 'Jan - Mrt' },
  { id: 'Q2-2026', label: 'Q2 2026', months: 'Apr - Jun' },
  { id: 'Q3-2026', label: 'Q3 2026', months: 'Jul - Sep' },
  { id: 'Q4-2026', label: 'Q4 2026', months: 'Okt - Dec' },
  { id: 'backlog', label: 'Backlog', months: 'Niet ingepland' }
];

const GENERAL_ROADMAP_ID = '__general__';

// Status-specific approval checks
const DRAFT_CHECKS = [
  { id: 'checkAnalyse', label: 'Analyse gedaan', short: 'Analyse' },
  { id: 'checkImpact', label: 'Impact bepaald', short: 'Impact' },
  { id: 'checkMT', label: 'MT akkoord', short: 'MT' },
  { id: 'checkPrio', label: 'Prioriteit bepaald', short: 'Prio' }
];

const READY_CHECKS = [
  { id: 'checkSpec', label: 'Specificatie compleet', short: 'Spec' },
  { id: 'checkDesign', label: 'Design goedgekeurd', short: 'Design' },
  { id: 'checkTeam', label: 'Team toegewezen', short: 'Team' },
  { id: 'checkPlanning', label: 'Planning gemaakt', short: 'Planning' }
];

const DEV_CHECKS = [
  { id: 'checkDev', label: 'Development klaar', short: 'Dev' },
  { id: 'checkTest', label: 'Getest', short: 'Test' },
  { id: 'checkReview', label: 'Code review', short: 'Review' },
  { id: 'checkDocs', label: 'Documentatie', short: 'Docs' }
];

const DONE_CHECKS = [
  { id: 'checkDeployed', label: 'Deployed', short: 'Deploy' },
  { id: 'checkAccepted', label: 'Geaccepteerd', short: 'Accept' },
  { id: 'checkComms', label: 'Gecommuniceerd', short: 'Comms' },
  { id: 'checkClosed', label: 'Ticket gesloten', short: 'Closed' }
];

const STATUS_CHECKS = {
  'draft': DRAFT_CHECKS,
  'ready': READY_CHECKS,
  'development': DEV_CHECKS,
  'done': DONE_CHECKS
};

const APP_VERSION = 'v3.2';

const GANTT_SWIMLANES = [
  { id: 'platform', label: 'GeriForce Platform Improvements', color: '#3498DB' },
  { id: 'service', label: 'GeriCall Service Enhancement', color: '#27AE60' },
  { id: 'innovation', label: 'Innovations', color: '#9B59B6' }
];

const DEVELOPER_ROLES = [
  { id: 'fullstack', label: 'Full Stack', color: '#9B59B6' },
  { id: 'backend', label: 'Back-end', color: '#3498DB' },
  { id: 'frontend', label: 'Front-end', color: '#27AE60' },
  { id: 'design', label: 'Design', color: '#1ABC9C' },
  { id: 'devops', label: 'DevOps', color: '#FF9800' }
];

const DEFAULT_DEVELOPERS = [
  { id: 'dev_allard', name: 'Allard', role: 'fullstack', color: '#9B59B6' },
  { id: 'dev_randall', name: 'Randall', role: 'backend', color: '#3498DB' },
  { id: 'dev_derick', name: 'Derick', role: 'frontend', color: '#27AE60' },
  { id: 'dev_kelvin', name: 'Kelvin', role: 'frontend', color: '#E67E22' },
  { id: 'dev_manon', name: 'Manon', role: 'frontend', color: '#1ABC9C' },
  { id: 'dev_karim', name: 'Karim', role: 'backend', color: '#00BCD4' },
  { id: 'dev_demmy', name: 'Demmy', role: 'frontend', color: '#8BC34A' }
];

const GANTT_MONTHS = [
  { id: '2026-01', label: 'Jan', quarter: 'Q1' },
  { id: '2026-02', label: 'Feb', quarter: 'Q1' },
  { id: '2026-03', label: 'Mrt', quarter: 'Q1' },
  { id: '2026-04', label: 'Apr', quarter: 'Q2' },
  { id: '2026-05', label: 'Mei', quarter: 'Q2' },
  { id: '2026-06', label: 'Jun', quarter: 'Q2' },
  { id: '2026-07', label: 'Jul', quarter: 'Q3' },
  { id: '2026-08', label: 'Aug', quarter: 'Q3' },
  { id: '2026-09', label: 'Sep', quarter: 'Q3' },
  { id: '2026-10', label: 'Okt', quarter: 'Q4' },
  { id: '2026-11', label: 'Nov', quarter: 'Q4' },
  { id: '2026-12', label: 'Dec', quarter: 'Q4' }
];

const COST_TYPES = [
  { id: 'behoud', label: 'Behoud', color: '#7F8C8D', icon: '🔧', description: 'Run & Maintain' },
  { id: 'innovatie', label: 'Innovatie', color: '#27AE60', icon: '🚀', description: 'Nieuwe features & verbeteringen' },
  { id: 'uitbreiding', label: 'Uitbreiding Team', color: '#9B59B6', icon: '👥', description: 'Capaciteitsuitbreiding' }
];

const REVENUE_2026 = 11400000; // €11.4M omzet prognose

const DEFAULT_IT_COSTS = [
  // === HOSTING & INFRASTRUCTURE ===
  { id: 'cost_azure', name: 'Microsoft Azure NL', amount: 4000, frequency: 'monthly', category: 'hosting', entity: 'geriforce_bv', costType: 'behoud', notes: 'Hosting, backup & DR' },
  
  // === TELEPHONY ===
  { id: 'cost_twilio', name: 'Twilio', amount: 4700, frequency: 'monthly', category: 'telephony', entity: 'geriforce_bv', costType: 'behoud', notes: 'Telefonie platform' },
  
  // === LICENSES ===
  { id: 'cost_ms_ireland', name: 'Microsoft (Ireland Operations)', amount: 2333, frequency: 'monthly', category: 'licenses', entity: 'geriforce_bv', costType: 'behoud', notes: 'M365, etc.' },
  { id: 'cost_ms_bv', name: 'Microsoft B.V. (Licenses)', amount: 417, frequency: 'monthly', category: 'licenses', entity: 'geriforce_bv', costType: 'behoud', notes: 'Overige licenties' },
  
  // === TOOLS & SOFTWARE ===
  { id: 'cost_conrad', name: 'ConRad', amount: 3000, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'behoud', notes: 'Planning/roostering systeem' },
  { id: 'cost_zendesk', name: 'Zendesk Suite Team', amount: 525, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'behoud', notes: 'Customer support, 10 agents' },
  { id: 'cost_contactcare', name: 'Contact Care', amount: 167, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'behoud', notes: 'Contact management' },
  { id: 'cost_jira', name: 'JIRA Software Standard', amount: 78, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'behoud', notes: 'Project management, 10 users' },
  { id: 'cost_confluence', name: 'Confluence Standard', amount: 62, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'behoud', notes: 'Documentatie/wiki, 10 users' },
  { id: 'cost_jpd', name: 'Jira Product Discovery', amount: 96, frequency: 'monthly', category: 'tools', entity: 'geriforce_bv', costType: 'innovatie', notes: 'Product roadmap, 10 creators' },
  
  // === OTHER ===
  { id: 'cost_dustin', name: 'Dustin', amount: 167, frequency: 'monthly', category: 'other', entity: 'geriforce_bv', costType: 'behoud', notes: 'Hardware/supplies' },
  
  // === PERSONNEL - INTERN ===
  { id: 'cost_loonsom', name: 'Loonsom IT Team 2026', amount: 624750, frequency: 'yearly', category: 'personnel', entity: 'geriforce_bv', costType: 'behoud', notes: '+5% CPI NL t.o.v. 2025 (€595K), 7 FTE' },
  
  // === EXTERNAL / KOSTEN DERDEN ===
  { id: 'cost_blackbirds', name: 'BlackBirds', amount: 100000, frequency: 'yearly', category: 'external', entity: 'geriforce_bv', costType: 'innovatie', notes: 'Externe development partner' },
  { id: 'cost_auren', name: 'Auren', amount: 80000, frequency: 'yearly', category: 'external', entity: 'geriforce_bv', costType: 'innovatie', notes: 'Externe consultancy' },
  { id: 'cost_koen', name: 'Koen Molenaar Consulting', amount: 40000, frequency: 'yearly', category: 'external', entity: 'geriforce_bv', costType: 'innovatie', notes: 'IT Strategie & Product Management' },
  
  // === UITBREIDING TEAM (GEPROGNOTISEERD) ===
  { id: 'cost_extra_backend', name: 'Extra Backend Developer (Medio NL)', amount: 80000, frequency: 'yearly', category: 'personnel', entity: 'geriforce_bv', costType: 'uitbreiding', notes: 'Geprognotiseerd Q2-Q4, senior niveau' }
];

const COST_CATEGORIES = [
  { id: 'personnel', label: 'Personeelskosten', icon: '👥', color: '#9B59B6' },
  { id: 'external', label: 'Kosten Derden', icon: '🤝', color: '#E74C3C' },
  { id: 'hosting', label: 'Hosting & Infrastructure', icon: '☁️', color: '#3498DB' },
  { id: 'telephony', label: 'Telephony', icon: '📞', color: '#27AE60' },
  { id: 'tools', label: 'Tools & Software', icon: '🛠️', color: '#1ABC9C' },
  { id: 'licenses', label: 'Licenses', icon: '📜', color: '#E67E22' },
  { id: 'security', label: 'Security', icon: '🔒', color: '#34495E' },
  { id: 'other', label: 'Other', icon: '📦', color: '#7F8C8D' }
];

const COST_ENTITIES = [
  { id: 'geriforce_ltd', label: 'GeriForce LTD', color: '#3498DB' },
  { id: 'geriforce_bv', label: 'GeriForce B.V.', color: '#27AE60' },
  { id: 'gericall_bv', label: 'GeriCall B.V.', color: '#9B59B6' },
  { id: 'shared', label: 'Shared', color: '#E67E22' }
];

const COST_FREQUENCIES = [
  { id: 'monthly', label: 'Per maand', multiplier: 12 },
  { id: 'quarterly', label: 'Per kwartaal', multiplier: 4 },
  { id: 'yearly', label: 'Per jaar', multiplier: 1 },
  { id: 'onetime', label: 'Eenmalig', multiplier: 1 }
];

const EPIC_CONFIG = [
  { 
    id: 'supervisie', 
    ideaKey: 'supervisie',
    title: 'Proactieve Supervisie', 
    icon: '👨‍⚕️', 
    color: '#3498DB',
    description: 'Real-time monitoring en alerting voor betere patiëntenzorg',
    component: 'SupervisieDashboard'
  },
  { 
    id: 'salarisverwerking', 
    ideaKey: 'salarisverwerking',
    title: 'Salarisverwerking Vereenvoudigen', 
    icon: '💶', 
    color: '#27AE60',
    description: 'Geautomatiseerde en foutloze salarisadministratie',
    component: 'SalarisverwerkingDashboard'
  }
];

// SHA-256 hash functie
const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// SHA-256 hash van het correcte wachtwoord (NIET het wachtwoord zelf!)
// Standaard toegang (secties 1-5): GeriCall2026!
const VALID_HASH = 'c15c490cf0a029073e946f56061957ad4a2ad468874c1fb5ee59b12ef68de677';
// Admin toegang (secties 1-6 incl. kosten): AntsPants2000!
const ADMIN_HASH = '13a9ef0234b61819e6dae8cadb7f9f615fa63f4f5d16032cb1f4a5401c2b0931';

const JSONBIN_CONFIG = {
  API_KEY: '$2a$10$zWeDCcv/08dfJKpRyW6lv.HE2MG/Q9KitKQ6falrFUU.0z3UuDqCG',
  BIN_ID: '6967a935d0ea881f406b9f32'
};

export default function GeriCallInteractiveDemo() {
  const [currentView, setCurrentView] = useState('persona-journey');
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('user'); // 'user' or 'admin'
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [userName, setUserName] = useState('');
  const [loginName, setLoginName] = useState('');
  const [personas, setPersonas] = useState(SHARED_PERSONAS);
  const [comments, setComments] = useState(SHARED_COMMENTS);
  const [journeyStages, setJourneyStages] = useState(SHARED_JOURNEY_STAGES);
  const [journeyBundles, setJourneyBundles] = useState(SHARED_JOURNEY_BUNDLES || []);
  const [journeys, setJourneys] = useState([
    { id: 'consult', naam: 'GeriCall Consult Journey', stages: [], bundles: [] }
  ]);
  const [activeJourneyId, setActiveJourneyId] = useState('consult');
  const [strategicVisions, setStrategicVisions] = useState([]);
  const [businessVisions, setBusinessVisions] = useState([]);
  const [developers, setDevelopers] = useState(DEFAULT_DEVELOPERS);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [binId, setBinId] = useState(JSONBIN_CONFIG.BIN_ID || localStorage.getItem('gericall_bin_id'));
  
  // Refs
  const commentNameRef = useRef(null);
  const commentTextRef = useRef(null);
  const commentStatusRef = useRef(null);
  const commentJiraRef = useRef(null);
  const commentFteRef = useRef(null);
  const commentQuarterRef = useRef(null);
  const editNaamRef = useRef(null);
  const editBeschrijvingRef = useRef(null);
  const editRolRef = useRef(null);
  const editFrequentieRef = useRef(null);
  const editUitdagingenRef = useRef(null);
  const editBehoeftenRef = useRef(null);
  const editKpisRef = useRef(null);
  const editToekomstigSchermRef = useRef(null);
  const stageNaamRef = useRef(null);
  const stageBeschrijvingRef = useRef(null);
  const bundleNaamRef = useRef(null);
  const newNaamRef = useRef(null);
  const newBeschrijvingRef = useRef(null);
  const newRolRef = useRef(null);
  const newFrequentieRef = useRef(null);
  const newUitdagingenRef = useRef(null);
  const newBehoeftenRef = useRef(null);
  const newKpisRef = useRef(null);
  const newToekomstigSchermRef = useRef(null);
  const ganttTitleRef = useRef(null);
  const ganttSwimlaneRef = useRef(null);
  const ganttJiraRef = useRef(null);
  const ganttStartRef = useRef(null);
  const ganttEndRef = useRef(null);
  
  // Modal states
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentPersonaId, setCommentPersonaId] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editingPersona, setEditingPersona] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editIcon, setEditIcon] = useState('user');
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingStageId, setEditingStageId] = useState(null);
  const [stageForm, setStageForm] = useState({ stage: '', beschrijving: '', links: [], primaryPersona: '', type: 'step', yesBranch: '', noBranch: '', noBranchLabel: 'Nee' });
  const [stageLinks, setStageLinks] = useState([]);
  const [showNewPersonaModal, setShowNewPersonaModal] = useState(false);
  const [newPersonaOrg, setNewPersonaOrg] = useState('');
  const [newPersonaIcon, setNewPersonaIcon] = useState('user');
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [editingBundleId, setEditingBundleId] = useState(null);
  const [bundleForm, setBundleForm] = useState({ naam: '', stageIds: [] });
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [editingJourneyId, setEditingJourneyId] = useState(null);
  const journeyNaamRef = useRef(null);
  const [showVisionModal, setShowVisionModal] = useState(false);
  const [editingVisionId, setEditingVisionId] = useState(null);
  const [visionIcon, setVisionIcon] = useState('user');
  const [visionCategory, setVisionCategory] = useState('it');
  const visionNaamRef = useRef(null);
  const visionQuoteRef = useRef(null);
  
  // Accordion states - all collapsed by default
  const [expandedSections, setExpandedSections] = useState({
    speerpunten: false,
    roadmapKwartaal: false,
	epics: false,
    journeys: false,
    personas: false,
    gantt: false,
    costs: false
  });
  
  // IT Costs state
  const [itCosts, setItCosts] = useState(DEFAULT_IT_COSTS);
  
  // Gantt chart data
  const [ganttItems, setGanttItems] = useState([]);
  
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Cloud functions
  const loadFromCloud = async () => {
    if (JSONBIN_CONFIG.API_KEY === 'PLAK_HIER_JE_API_KEY') { setIsLoading(false); return; }
    const currentBinId = JSONBIN_CONFIG.BIN_ID || binId || localStorage.getItem('gericall_bin_id');
    if (!currentBinId) { setIsLoading(false); return; }
    try {
      const response = await fetch(`https://api.jsonbin.io/v3/b/${currentBinId}/latest`, {
        headers: { 'X-Master-Key': JSONBIN_CONFIG.API_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.record) {
          const cloudPersonas = data.record.personas;
          const cloudComments = data.record.comments || SHARED_COMMENTS;
          const cloudStages = data.record.journeyStages || SHARED_JOURNEY_STAGES;
          const cloudBundles = data.record.journeyBundles || [];
          const cloudVisions = data.record.strategicVisions || [];
          const cloudBusinessVisions = data.record.businessVisions || [];
          const cloudJourneys = data.record.journeys || [
            { id: 'consult', naam: 'GeriCall Consult Journey', stages: cloudStages, bundles: cloudBundles }
          ];
          const cloudGanttItems = data.record.ganttItems || [];
          const cloudDevelopers = data.record.developers || DEFAULT_DEVELOPERS;
          const cloudItCosts = data.record.itCosts || [];
          if (cloudPersonas && Object.keys(cloudPersonas).length > 0) {
            setPersonas(cloudPersonas);
            if (selectedPersona) {
              const personaKey = Object.keys(cloudPersonas).find(key => cloudPersonas[key].id === selectedPersona.id);
              if (personaKey) setSelectedPersona(cloudPersonas[personaKey]);
            }
          } else {
            await saveToCloud(SHARED_PERSONAS, cloudComments, cloudStages, cloudBundles, cloudVisions, cloudBusinessVisions, cloudJourneys);
          }
          setComments(cloudComments);
          setJourneyStages(cloudStages);
          setJourneyBundles(cloudBundles);
          setStrategicVisions(cloudVisions);
          setBusinessVisions(cloudBusinessVisions);
          setJourneys(cloudJourneys);
          setGanttItems(cloudGanttItems);
          setDevelopers(cloudDevelopers);
          setItCosts(cloudItCosts);
          if (cloudJourneys.length > 0 && !cloudJourneys.find(j => j.id === activeJourneyId)) {
            setActiveJourneyId(cloudJourneys[0].id);
          }
          setLastSync(new Date().toLocaleTimeString('nl-NL'));
        }
      }
    } catch (error) { console.error('Fout bij laden:', error); }
    setIsLoading(false);
  };
  
  const saveToCloud = async (newPersonas, newComments, newStages, newBundles, newVisions, newBusinessVisions, newJourneys, newGanttItems, newDevelopers, newItCosts) => {
    if (JSONBIN_CONFIG.API_KEY === 'PLAK_HIER_JE_API_KEY') return;
    const dataToSave = {
      personas: newPersonas || personas,
      comments: newComments || comments,
      journeyStages: newStages || journeyStages,
      journeyBundles: newBundles || journeyBundles,
      strategicVisions: newVisions || strategicVisions,
      businessVisions: newBusinessVisions || businessVisions,
      journeys: newJourneys || journeys,
      ganttItems: newGanttItems || ganttItems,
      developers: newDevelopers || developers,
      itCosts: newItCosts || itCosts,
      lastUpdated: new Date().toISOString(),
      updatedBy: userName || 'Unknown'
    };
    try {
      const currentBinId = JSONBIN_CONFIG.BIN_ID || binId || localStorage.getItem('gericall_bin_id');
      if (currentBinId) {
        const response = await fetch(`https://api.jsonbin.io/v3/b/${currentBinId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Master-Key': JSONBIN_CONFIG.API_KEY },
          body: JSON.stringify(dataToSave)
        });
        if (response.ok) setLastSync(new Date().toLocaleTimeString('nl-NL'));
      } else {
        const response = await fetch('https://api.jsonbin.io/v3/b', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Master-Key': JSONBIN_CONFIG.API_KEY, 'X-Bin-Name': 'GeriCall-Demo-Data' },
          body: JSON.stringify(dataToSave)
        });
        if (response.ok) {
          const result = await response.json();
          const newBinId = result.metadata.id;
          setBinId(newBinId);
          localStorage.setItem('gericall_bin_id', newBinId);
          setLastSync(new Date().toLocaleTimeString('nl-NL'));
        }
      }
    } catch (error) { console.error('Fout bij opslaan:', error); }
  };

useEffect(() => {
  const loggedIn = sessionStorage.getItem('gericall_authenticated');
  const savedName = sessionStorage.getItem('gericall_username');
  const savedRole = sessionStorage.getItem('gericall_role');
  if (loggedIn === 'true') { 
    setIsAuthenticated(true); 
    if (savedName) setUserName(savedName);
    if (savedRole) setUserRole(savedRole);
  }
  loadFromCloud();
  
  // Deep link handling
  const handleHashChange = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['supervisie', 'salarisverwerking', 'epics'].includes(hash)) {
      setCurrentView(hash);
    }
  };
  
  // Check on load
  handleHashChange();
  
  // Listen for hash changes
  window.addEventListener('hashchange', handleHashChange);
  return () => window.removeEventListener('hashchange', handleHashChange);
}, []);

  // Comment functions
  const openCommentModal = (personaId = null, existingComment = null) => { 
    setCommentPersonaId(personaId); 
    setEditingComment(existingComment);
    setShowCommentModal(true); 
  };
  const closeCommentModal = () => { 
    setShowCommentModal(false); 
    setCommentPersonaId(null); 
    setEditingComment(null);
  };
  const addComment = async () => {
    const name = commentNameRef.current?.value?.trim() || userName || '';
    const text = commentTextRef.current?.value?.trim() || '';
    const status = commentStatusRef.current?.value || 'draft';
    const jiraLink = commentJiraRef.current?.value?.trim() || '';
    const fte = parseFloat(commentFteRef.current?.value) || 0;
    const quarter = commentQuarterRef.current?.value || 'backlog';
    const targetPersonaId = commentPersonaId || GENERAL_ROADMAP_ID;
    if (!name || !text) { alert('Vul alsjeblieft naam en beschrijving in!'); return; }
    
    // Get all checkbox values from all statuses
    const checkValues = {};
    Object.values(STATUS_CHECKS).flat().forEach(check => {
      checkValues[check.id] = document.getElementById(check.id)?.checked || false;
    });
    
    let newComments;
    if (editingComment) {
      newComments = { 
        ...comments, 
        [targetPersonaId]: (comments[targetPersonaId] || []).map(c => 
          c.id === editingComment.id 
            ? { ...c, name, text, status, jiraLink, fte, quarter, ...checkValues }
            : c
        )
      };
    } else {
      const newComment = { id: Date.now(), name, text, status, jiraLink, fte, quarter, ...checkValues, personaId: targetPersonaId, timestamp: new Date().toISOString(), date: new Date().toLocaleDateString('nl-NL') };
      newComments = { ...comments, [targetPersonaId]: [...(comments[targetPersonaId] || []), newComment] };
    }
    
    setComments(newComments);
    await saveToCloud(personas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
    closeCommentModal();
  };
  
  const toggleCommentCheck = async (personaId, commentId, checkField) => {
    const newComments = { 
      ...comments, 
      [personaId]: comments[personaId].map(c => 
        c.id === commentId ? { ...c, [checkField]: !c[checkField] } : c
      )
    };
    setComments(newComments);
    await saveToCloud(personas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
  };
  
  const getChecksForStatus = (status) => STATUS_CHECKS[status] || [];
  
  const countChecksComplete = (item) => {
    const checks = getChecksForStatus(item.status || 'draft');
    return checks.filter(check => item[check.id]).length;
  };
  
  const allChecksComplete = (item) => {
    const checks = getChecksForStatus(item.status || 'draft');
    return checks.length > 0 && checks.every(check => item[check.id]);
  };
  const updateCommentStatus = async (personaId, commentId, newStatus) => {
    const newComments = { 
      ...comments, 
      [personaId]: comments[personaId].map(c => c.id === commentId ? { ...c, status: newStatus } : c) 
    };
    setComments(newComments);
    await saveToCloud(personas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
  };
  const updateCommentQuarter = async (personaId, commentId, newQuarter) => {
    const newComments = { 
      ...comments, 
      [personaId]: comments[personaId].map(c => c.id === commentId ? { ...c, quarter: newQuarter } : c) 
    };
    setComments(newComments);
    await saveToCloud(personas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
  };
  const deleteComment = async (personaId, commentId) => {
    if (window.confirm('Weet je zeker dat je dit roadmap item wilt verwijderen?')) {
      const newComments = { ...comments, [personaId]: comments[personaId].filter(c => c.id !== commentId) };
      setComments(newComments);
      await saveToCloud(personas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
    }
  };
  
  // Get all roadmap items across all personas
  const getAllRoadmapItems = () => {
    const allItems = [];
    Object.entries(comments).forEach(([personaId, items]) => {
      (items || []).forEach(item => {
        const persona = personaId === GENERAL_ROADMAP_ID 
          ? { naam: 'Algemeen', id: GENERAL_ROADMAP_ID }
          : Object.values(personas).find(p => p.id === personaId);
        allItems.push({ ...item, personaId, personaNaam: persona?.naam || 'Onbekend' });
      });
    });
    return allItems;
  };

  // Persona functions
  const startEditingPersona = (persona, personaKey) => {
    setEditingPersona(personaKey);
    setEditIcon(persona.icon || 'user');
    setEditForm({
      naam: persona.naam || '', beschrijving: persona.beschrijving || '', frequentie: persona.frequentie || '',
      rol: persona.rol || '', uitdagingen: (persona.uitdagingen || []).join('\n'), behoeften: (persona.behoeften || []).join('\n'),
      kpis: (persona.kpis || []).join('\n'), toekomstigScherm: persona.toekomstigScherm || ''
    });
  };
  const deletePersona = async (personaKey) => {
    const persona = personas[personaKey];
    if (!persona) return;
    if (window.confirm(`Weet je zeker dat je "${persona.naam}" wilt verwijderen?`)) {
      const newPersonas = { ...personas }; delete newPersonas[personaKey];
      const newComments = { ...comments }; delete newComments[persona.id];
      setPersonas(newPersonas); setComments(newComments);
      if (selectedPersona?.id === persona.id) setSelectedPersona(null);
      await saveToCloud(newPersonas, newComments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
    }
  };
  const savePersonaEdit = async () => {
    const naam = editNaamRef.current?.value ?? editForm.naam ?? '';
    const beschrijving = editBeschrijvingRef.current?.value ?? editForm.beschrijving ?? '';
    const rol = editRolRef.current?.value ?? editForm.rol ?? '';
    const frequentie = editFrequentieRef.current?.value ?? editForm.frequentie ?? '';
    const uitdagingen = editUitdagingenRef.current?.value ?? editForm.uitdagingen ?? '';
    const behoeften = editBehoeftenRef.current?.value ?? editForm.behoeften ?? '';
    const kpis = editKpisRef.current?.value ?? editForm.kpis ?? '';
    const toekomstigScherm = editToekomstigSchermRef.current?.value ?? editForm.toekomstigScherm ?? '';
    const newPersonas = {
      ...personas,
      [editingPersona]: {
        ...personas[editingPersona], naam, beschrijving, frequentie, rol, icon: editIcon,
        uitdagingen: uitdagingen.split('\n').filter(line => line.trim()),
        behoeften: behoeften.split('\n').filter(line => line.trim()),
        kpis: kpis.split('\n').filter(line => line.trim()), toekomstigScherm
      }
    };
    setPersonas(newPersonas);
    setSelectedPersona(newPersonas[editingPersona]);
    await saveToCloud(newPersonas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys);
    setEditingPersona(null); setEditForm({});
  };
  const cancelPersonaEdit = () => { setEditingPersona(null); setEditForm({}); };

  // New persona functions
  const openNewPersonaModal = (organisatie) => { setNewPersonaOrg(organisatie); setNewPersonaIcon('user'); setShowNewPersonaModal(true); };
  const closeNewPersonaModal = () => { setShowNewPersonaModal(false); setNewPersonaOrg(''); setNewPersonaIcon('user'); };
  const saveNewPersona = async () => {
    const naam = newNaamRef.current?.value?.trim() || '';
    if (!naam) { alert('Vul minimaal een naam in!'); return; }
    const timestamp = Date.now();
    const personaKey = naam.toLowerCase().replace(/[^a-z0-9]/g, '') + '_' + timestamp;
    const personaId = personaKey.substring(0, 8);
    const newPersona = {
      id: personaId, naam, organisatie: newPersonaOrg,
      beschrijving: newBeschrijvingRef.current?.value?.trim() || '',
      frequentie: newFrequentieRef.current?.value?.trim() || '',
      rol: newRolRef.current?.value?.trim() || '', icon: newPersonaIcon,
      uitdagingen: (newUitdagingenRef.current?.value || '').split('\n').filter(line => line.trim()),
      behoeften: (newBehoeftenRef.current?.value || '').split('\n').filter(line => line.trim()),
      kpis: (newKpisRef.current?.value || '').split('\n').filter(line => line.trim()),
      toekomstigScherm: newToekomstigSchermRef.current?.value?.trim() || '',
      huidigeSchermen: [], mockups: []
    };
    const newPersonas = { ...personas, [personaKey]: newPersona };
    setPersonas(newPersonas); setSelectedPersona(newPersona);
    await saveToCloud(newPersonas, comments, journeyStages, journeyBundles, strategicVisions);
    closeNewPersonaModal();
  };

  // Stage functions
  const openStageModal = (stage = null) => {
    if (stage) {
      setEditingStageId(stage.id);
      setStageForm({ 
        stage: stage.stage || '', 
        beschrijving: stage.beschrijving || '', 
        links: stage.links || [], 
        primaryPersona: stage.primaryPersona || '',
        type: stage.type || 'step',
        yesBranch: stage.yesBranch || '',
        noBranch: stage.noBranch || '',
        noBranchLabel: stage.noBranchLabel || 'Nee'
      });
      setStageLinks(stage.links || []);
    } else {
      setEditingStageId(null);
      setStageForm({ stage: '', beschrijving: '', links: [], primaryPersona: '', type: 'step', yesBranch: '', noBranch: '', noBranchLabel: 'Nee' });
      setStageLinks([]);
    }
    setShowStageModal(true);
  };
  const closeStageModal = () => { setShowStageModal(false); setEditingStageId(null); setStageForm({ stage: '', beschrijving: '', links: [], primaryPersona: '' }); setStageLinks([]); };
  const addStageLink = () => { setStageLinks([...stageLinks, { naam: '', url: '' }]); };
  const updateStageLink = (index, field, value) => { const newLinks = [...stageLinks]; newLinks[index][field] = value; setStageLinks(newLinks); };
  const removeStageLink = (index) => { setStageLinks(stageLinks.filter((_, i) => i !== index)); };
  const saveStage = async () => {
    const naam = stageNaamRef.current?.value?.trim() || '';
    const beschrijving = stageBeschrijvingRef.current?.value?.trim() || '';
    const validLinks = stageLinks.filter(link => link.naam.trim() && link.url.trim());
    if (!naam) { alert('Vul minimaal een naam in!'); return; }
    
    const activeJourney = getActiveJourney();
    const currentStages = activeJourney.stages || [];
    
    const stageData = { 
      stage: naam, 
      beschrijving, 
      links: validLinks, 
      primaryPersona: stageForm.primaryPersona,
      type: stageForm.type || 'step',
      yesBranch: stageForm.yesBranch || '',
      noBranch: stageForm.noBranch || '',
      noBranchLabel: stageForm.noBranchLabel || 'Nee'
    };
    
    let newStages;
    if (editingStageId) {
      newStages = currentStages.map(s => s.id === editingStageId ? { ...s, ...stageData } : s);
    } else {
      newStages = [...currentStages, { id: 'stage_' + Date.now(), ...stageData }];
    }
    
    const newJourneys = journeys.map(j => j.id === activeJourneyId ? { ...j, stages: newStages } : j);
    setJourneys(newJourneys);
    await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    closeStageModal();
  };
  const deleteStage = async (stageId) => {
    const activeJourney = getActiveJourney();
    const currentStages = activeJourney.stages || [];
    const stage = currentStages.find(s => s.id === stageId);
    if (!stage) return;
    if (window.confirm(`Weet je zeker dat je "${stage.stage}" wilt verwijderen?`)) {
      const newStages = currentStages.filter(s => s.id !== stageId);
      const currentBundles = activeJourney.bundles || [];
      const newBundles = currentBundles.map(b => ({ ...b, stageIds: b.stageIds.filter(id => id !== stageId) }));
      
      const newJourneys = journeys.map(j => j.id === activeJourneyId ? { ...j, stages: newStages, bundles: newBundles } : j);
      setJourneys(newJourneys);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    }
  };
  const moveStage = async (stageId, direction) => {
    const activeJourney = getActiveJourney();
    const currentStages = activeJourney.stages || [];
    const currentIndex = currentStages.findIndex(s => s.id === stageId);
    if (currentIndex === -1) return;
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= currentStages.length) return;
    const newStages = [...currentStages];
    [newStages[currentIndex], newStages[newIndex]] = [newStages[newIndex], newStages[currentIndex]];
    
    const newJourneys = journeys.map(j => j.id === activeJourneyId ? { ...j, stages: newStages } : j);
    setJourneys(newJourneys);
    await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
  };

  // Bundle functions
  const openBundleModal = (bundle = null) => {
    if (bundle) {
      setEditingBundleId(bundle.id);
      setBundleForm({ naam: bundle.naam || '', stageIds: bundle.stageIds || [] });
    } else {
      setEditingBundleId(null);
      setBundleForm({ naam: '', stageIds: [] });
    }
    setShowBundleModal(true);
  };
  const closeBundleModal = () => { setShowBundleModal(false); setEditingBundleId(null); setBundleForm({ naam: '', stageIds: [] }); };
  const toggleStageInBundle = (stageId) => {
    if (bundleForm.stageIds.includes(stageId)) {
      setBundleForm({ ...bundleForm, stageIds: bundleForm.stageIds.filter(id => id !== stageId) });
    } else {
      setBundleForm({ ...bundleForm, stageIds: [...bundleForm.stageIds, stageId] });
    }
  };
  const saveBundle = async () => {
    const naam = bundleNaamRef.current?.value?.trim() || '';
    if (!naam || bundleForm.stageIds.length < 2) { alert('Vul een naam in en selecteer minimaal 2 stappen!'); return; }
    const newBundle = { id: editingBundleId || 'bundle_' + Date.now(), naam, stageIds: bundleForm.stageIds };
    const newJourneys = journeys.map(j => {
      if (j.id === activeJourneyId) {
        if (editingBundleId) {
          return { ...j, bundles: j.bundles.map(b => b.id === editingBundleId ? newBundle : b) };
        } else {
          return { ...j, bundles: [...j.bundles, newBundle] };
        }
      }
      return j;
    });
    setJourneys(newJourneys);
    await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    closeBundleModal();
  };
  const deleteBundle = async (bundleId) => {
    if (window.confirm('Weet je zeker dat je deze bundel wilt verwijderen?')) {
      const newJourneys = journeys.map(j => {
        if (j.id === activeJourneyId) {
          return { ...j, bundles: (j.bundles || []).filter(b => b.id !== bundleId) };
        }
        return j;
      });
      setJourneys(newJourneys);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    }
  };

  // Journey functions
  const getActiveJourney = () => {
    return journeys.find(j => j.id === activeJourneyId) || { id: 'default', naam: 'Default', stages: journeyStages, bundles: journeyBundles };
  };
  
  const openJourneyModal = (journey = null) => {
    if (journey) {
      setEditingJourneyId(journey.id);
    } else {
      setEditingJourneyId(null);
    }
    setShowJourneyModal(true);
  };
  
  const closeJourneyModal = () => {
    setShowJourneyModal(false);
    setEditingJourneyId(null);
  };
  
  const saveJourney = async () => {
    const naam = journeyNaamRef.current?.value?.trim() || '';
    if (!naam) { alert('Vul een naam in voor de procesgang!'); return; }
    
    let newJourneys;
    if (editingJourneyId) {
      newJourneys = journeys.map(j => j.id === editingJourneyId ? { ...j, naam } : j);
    } else {
      const newJourney = {
        id: 'journey_' + Date.now(),
        naam,
        stages: [],
        bundles: []
      };
      newJourneys = [...journeys, newJourney];
      setActiveJourneyId(newJourney.id);
    }
    setJourneys(newJourneys);
    await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    closeJourneyModal();
  };
  
  const deleteJourney = async (journeyId) => {
    if (journeys.length <= 1) {
      alert('Je moet minimaal één procesgang hebben!');
      return;
    }
    const journey = journeys.find(j => j.id === journeyId);
    if (!journey) return;
    if (window.confirm(`Weet je zeker dat je "${journey.naam}" wilt verwijderen?`)) {
      const newJourneys = journeys.filter(j => j.id !== journeyId);
      setJourneys(newJourneys);
      if (activeJourneyId === journeyId) {
        setActiveJourneyId(newJourneys[0].id);
      }
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
    }
  };

  const moveJourney = async (journeyId, direction) => {
    const currentIndex = journeys.findIndex(j => j.id === journeyId);
    if (currentIndex === -1) return;
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= journeys.length) return;
    const newJourneys = [...journeys];
    [newJourneys[currentIndex], newJourneys[newIndex]] = [newJourneys[newIndex], newJourneys[currentIndex]];
    setJourneys(newJourneys);
    await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, newJourneys);
  };

  // Vision functions
  const openVisionModal = (vision = null, category = 'it') => {
    if (vision) {
      setEditingVisionId(vision.id);
      setVisionIcon(vision.icon || 'user');
      setVisionCategory(category);
    } else {
      setEditingVisionId(null);
      setVisionIcon('user');
      setVisionCategory(category);
    }
    setShowVisionModal(true);
  };
  const closeVisionModal = () => { setShowVisionModal(false); setEditingVisionId(null); setVisionIcon('user'); setVisionCategory('it'); };
  const saveVision = async () => {
    const naam = visionNaamRef.current?.value?.trim() || '';
    const quote = visionQuoteRef.current?.value?.trim() || '';
    if (!naam || !quote) { alert('Vul naam en speerpunten in!'); return; }
    
    if (visionCategory === 'it') {
      let newVisions;
      if (editingVisionId) {
        newVisions = strategicVisions.map(v => v.id === editingVisionId ? { ...v, naam, quote, icon: visionIcon } : v);
      } else {
        newVisions = [...strategicVisions, { id: 'vision_' + Date.now(), naam, quote, icon: visionIcon }];
      }
      setStrategicVisions(newVisions);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, newVisions, businessVisions, journeys);
    } else {
      let newVisions;
      if (editingVisionId) {
        newVisions = businessVisions.map(v => v.id === editingVisionId ? { ...v, naam, quote, icon: visionIcon } : v);
      } else {
        newVisions = [...businessVisions, { id: 'bvision_' + Date.now(), naam, quote, icon: visionIcon }];
      }
      setBusinessVisions(newVisions);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, newVisions, journeys);
    }
    closeVisionModal();
  };
  const deleteVision = async (visionId, category = 'it') => {
    if (window.confirm('Weet je zeker dat je deze visie wilt verwijderen?')) {
      if (category === 'it') {
        const newVisions = strategicVisions.filter(v => v.id !== visionId);
        setStrategicVisions(newVisions);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, newVisions, businessVisions);
      } else {
        const newVisions = businessVisions.filter(v => v.id !== visionId);
        setBusinessVisions(newVisions);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, newVisions);
      }
    }
  };

  // Auth functions
  const handleLogin = async () => {
    if (!loginName.trim()) { alert('Vul je naam in!'); return; }
    const inputHash = await hashPassword(password);
    // DEBUG: Uncomment volgende regel om hash te zien, daarna weer uitzetten!
    // console.log('Hash van ingevoerd wachtwoord:', inputHash);
    
    let role = null;
    if (inputHash === ADMIN_HASH) {
      role = 'admin';
    } else if (inputHash === VALID_HASH) {
      role = 'user';
    }
    
    if (role) {
      setIsAuthenticated(true); 
      setUserName(loginName.trim());
      setUserRole(role);
      sessionStorage.setItem('gericall_authenticated', 'true');
      sessionStorage.setItem('gericall_username', loginName.trim());
      sessionStorage.setItem('gericall_role', role);
      setLoginError(false);
    } else { 
      setLoginError(true); 
      setPassword(''); 
    }
  };
  const handleKeyPress = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleLogin(); } };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole('user');
    sessionStorage.removeItem('gericall_authenticated');
    sessionStorage.removeItem('gericall_username');
    sessionStorage.removeItem('gericall_role');
    setPassword(''); setLoginName(''); setUserName('');
    setCurrentView('persona-journey'); setSelectedPersona(null);
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="w-full max-w-md">
          <div className="bg-white p-6 sm:p-8" style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '12px', padding: '8px' }}>
                <img src={logo} alt="GeriCall Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#2C3E50' }}>GeriCall Demo</h1>
              <p className="text-xs sm:text-sm" style={{ color: '#7F8C8D' }}>Interactive Platform Preview</p>
            </div>
            <div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2C3E50' }}>Je naam</label>
                <input type="text" value={loginName} onChange={(e) => setLoginName(e.target.value)} onKeyPress={handleKeyPress}
                  placeholder="bijv. Koen M." className="w-full px-4 py-3 text-base"
                  style={{ border: '2px solid #BDC3C7', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} autoFocus />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2" style={{ color: '#2C3E50' }}>Wachtwoord</label>
                <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setLoginError(false); }} onKeyPress={handleKeyPress}
                  placeholder="Voer wachtwoord in..." className="w-full px-4 py-3 text-base"
                  style={{ border: `2px solid ${loginError ? '#E67332' : '#BDC3C7'}`, borderRadius: '8px', outline: 'none', backgroundColor: loginError ? '#FFE5D9' : 'white', boxSizing: 'border-box' }} />
                {loginError && <p className="mt-2 text-sm font-semibold" style={{ color: '#E67332' }}>❌ Onjuist wachtwoord.</p>}
              </div>
              <button onClick={handleLogin} className="w-full py-3 font-bold text-white" style={{ backgroundColor: '#FF8C42', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>Toegang tot demo</button>
            </div>
            <p className="text-xs text-center mt-6" style={{ color: '#BDC3C7' }}>© 2026 GeriCall - {APP_VERSION}</p>
          </div>
        </div>
      </div>
    );
  }

  const navigateTo = (view) => { 
  setCurrentView(view); 
  window.scrollTo(0, 0);
  // Update URL hash for deep linking (only for epic views)
  if (['supervisie', 'salarisverwerking', 'epics'].includes(view)) {
    window.history.pushState(null, '', `#${view}`);
  } else {
    window.history.pushState(null, '', window.location.pathname);
  }
};

  const BackButton = () => (
    <button onClick={() => navigateTo('persona-journey')} className="flex items-center space-x-2 mb-4 px-4 py-2"
      style={{ backgroundColor: '#ECF0F1', color: '#2C3E50', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
      <span>←</span><span className="font-semibold">Terug naar Persona Journey</span>
    </button>
  );

  const getPersonaIcon = (iconId, size = 6) => {
    const iconConfig = PERSONA_ICONS.find(i => i.id === iconId);
    if (iconConfig) { const IconComponent = iconConfig.icon; return <IconComponent className={`w-${size} h-${size}`} />; }
    return <User className={`w-${size} h-${size}`} />;
  };

  const IconSelector = ({ selectedIcon, onSelectIcon }) => (
    <div>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>Icoon</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px', border: '2px solid #BDC3C7', maxHeight: '200px', overflowY: 'auto' }}>
        {PERSONA_ICONS.map((iconConfig) => {
          const IconComponent = iconConfig.icon;
          const isSelected = selectedIcon === iconConfig.id;
          return (
            <button key={iconConfig.id} type="button" title={iconConfig.name} onClick={() => onSelectIcon(iconConfig.id)}
              style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                border: isSelected ? '2px solid #FF8C42' : '2px solid transparent', backgroundColor: isSelected ? '#FFE5D9' : 'white',
                color: isSelected ? '#FF8C42' : '#7F8C8D', cursor: 'pointer' }}>
              <IconComponent className="w-5 h-5" />
            </button>
          );
        })}
      </div>
    </div>
  );

  // ============================================
  // ACCORDION SECTION COMPONENT
  // ============================================
  const AccordionSection = ({ id, title, icon, isExpanded, onToggle, children, badge }) => (
    <div className="mb-3 sm:mb-4 bg-white" style={{ borderRadius: '12px', overflow: 'hidden', border: '2px solid #ECF0F1' }}>
      <button
        onClick={() => onToggle(id)}
        className="w-full p-3 sm:p-4 flex items-center justify-between"
        style={{ backgroundColor: isExpanded ? '#FF8C42' : 'white', color: isExpanded ? 'white' : '#2C3E50', border: 'none', cursor: 'pointer', transition: 'all 0.3s ease' }}
      >
        <div className="flex items-center space-x-2 sm:space-x-3">
          <span className="text-lg sm:text-2xl">{icon}</span>
          <h2 className="text-sm sm:text-lg font-bold">{title}</h2>
          {badge && <span className="hidden sm:inline text-xs" style={{ padding: '2px 8px', borderRadius: '12px', backgroundColor: isExpanded ? 'rgba(255,255,255,0.3)' : '#FFE5D9', color: isExpanded ? 'white' : '#FF8C42' }}>{badge}</span>}
        </div>
        <div style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', fontSize: '16px' }}>
          ▼
        </div>
      </button>
      {isExpanded && (
        <div className="p-3 sm:p-5" style={{ borderTop: '2px solid #ECF0F1' }}>
          {children}
        </div>
      )}
    </div>
  );

  // ============================================
  // GANTT CHART COMPONENT - TIMELINE VIEW
  // ============================================
  const GanttChart = () => {
    const [showGanttModal, setShowGanttModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingGanttItem, setEditingGanttItem] = useState(null);
    const [selectedImportItems, setSelectedImportItems] = useState([]);
    const [devsByMonth, setDevsByMonth] = useState({});
    const [modalStartMonth, setModalStartMonth] = useState('2026-01');
    const [modalEndMonth, setModalEndMonth] = useState('2026-03');
    const [modalTitle, setModalTitle] = useState('');
    const [modalSwimlane, setModalSwimlane] = useState('platform');
    const [modalJiraLink, setModalJiraLink] = useState('');
    const [hoveredItem, setHoveredItem] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [showDevModal, setShowDevModal] = useState(false);
    const [editingDev, setEditingDev] = useState(null);
    const [devForm, setDevForm] = useState({ name: '', role: 'frontend', color: '#3498DB' });
    
    const DEV_COLORS = ['#3498DB', '#27AE60', '#9B59B6', '#E67E22', '#1ABC9C', '#00BCD4', '#8BC34A', '#FF9800', '#607D8B', '#795548'];
    
    const openDevModal = (dev = null) => {
      if (dev) {
        setEditingDev(dev);
        setDevForm({ name: dev.name || '', role: dev.role || 'frontend', color: dev.color || '#3498DB' });
      } else {
        setEditingDev(null);
        setDevForm({ name: '', role: 'frontend', color: DEV_COLORS[developers.length % DEV_COLORS.length] });
      }
      setShowDevModal(true);
    };
    
    const closeDevModal = () => { setShowDevModal(false); setEditingDev(null); setDevForm({ name: '', role: 'frontend', color: '#3498DB' }); };
    
    const saveDev = async () => {
      if (!devForm.name) { alert('Vul een naam in!'); return; }
      let newDevs;
      if (editingDev) {
        newDevs = developers.map(d => d.id === editingDev.id ? { ...d, ...devForm } : d);
      } else {
        newDevs = [...developers, { id: 'dev_' + Date.now(), ...devForm }];
      }
      setDevelopers(newDevs);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, ganttItems, newDevs, itCosts);
      closeDevModal();
    };
    
    const deleteDev = async (devId) => {
      if (window.confirm('Weet je zeker dat je dit teamlid wilt verwijderen?')) {
        const newDevs = developers.filter(d => d.id !== devId);
        setDevelopers(newDevs);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, ganttItems, newDevs, itCosts);
      }
    };
    
    // Extract JIRA ticket from URL
    const extractJiraTicket = (url) => {
      if (!url) return '';
      const match = url.match(/([A-Z]+-\d+)/i);
      return match ? match[1].toUpperCase() : '';
    };
    
    const getImportableItems = () => {
      const allItems = getAllRoadmapItems();
      const importedIds = ganttItems.filter(g => g.sourceId).map(g => g.sourceId);
      return allItems.filter(item => !importedIds.includes(`${item.personaId}-${item.id}`));
    };
    
    const quarterToStartMonth = (quarter) => {
      const mapping = { 'Q1-2026': '2026-01', 'Q2-2026': '2026-04', 'Q3-2026': '2026-07', 'Q4-2026': '2026-10', 'backlog': '2026-01' };
      return mapping[quarter] || '2026-01';
    };
    
    const quarterToEndMonth = (quarter) => {
      const mapping = { 'Q1-2026': '2026-03', 'Q2-2026': '2026-06', 'Q3-2026': '2026-09', 'Q4-2026': '2026-12', 'backlog': '2026-03' };
      return mapping[quarter] || '2026-03';
    };
    
    const openGanttModal = (item = null) => {
      if (item) {
        setModalTitle(item.title || '');
        setModalSwimlane(item.swimlane || 'platform');
        setModalJiraLink(item.jiraLink || '');
        setModalStartMonth(item.startMonth || '2026-01');
        setModalEndMonth(item.endMonth || '2026-03');
        const convertedDevsByMonth = {};
        if (item.devsByMonth) {
          Object.entries(item.devsByMonth).forEach(([monthId, data]) => {
            if (Array.isArray(data)) {
              convertedDevsByMonth[monthId] = {};
              data.forEach(devId => { convertedDevsByMonth[monthId][devId] = 100; });
            } else if (typeof data === 'object') {
              convertedDevsByMonth[monthId] = { ...data };
            }
          });
        }
        setDevsByMonth(convertedDevsByMonth);
      } else {
        setModalTitle(''); setModalSwimlane('platform'); setModalJiraLink('');
        setModalStartMonth('2026-01'); setModalEndMonth('2026-03'); setDevsByMonth({});
      }
      setEditingGanttItem(item);
      setShowGanttModal(true);
    };
    
    const closeGanttModal = () => {
      setShowGanttModal(false); setEditingGanttItem(null);
      setModalTitle(''); setModalSwimlane('platform'); setModalJiraLink('');
      setModalStartMonth('2026-01'); setModalEndMonth('2026-03'); setDevsByMonth({});
    };
    
    const openImportModal = () => { setSelectedImportItems([]); setShowImportModal(true); };
    const closeImportModal = () => { setShowImportModal(false); setSelectedImportItems([]); };
    
    const toggleImportItem = (itemKey) => {
      if (selectedImportItems.includes(itemKey)) {
        setSelectedImportItems(selectedImportItems.filter(k => k !== itemKey));
      } else {
        setSelectedImportItems([...selectedImportItems, itemKey]);
      }
    };
    
    const importSelectedItems = async () => {
      const importableItems = getImportableItems();
      const itemsToImport = importableItems.filter(item => selectedImportItems.includes(`${item.personaId}-${item.id}`));
      const newGanttItemsToAdd = itemsToImport.map(item => ({
        id: 'gantt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        title: item.text, startMonth: quarterToStartMonth(item.quarter), endMonth: quarterToEndMonth(item.quarter),
        swimlane: 'platform', sourceId: `${item.personaId}-${item.id}`, sourcePersona: item.personaNaam,
        sourceStatus: item.status, fte: item.fte, jiraLink: item.jiraLink || '', devsByMonth: {}
      }));
      const newGanttItems = [...ganttItems, ...newGanttItemsToAdd];
      setGanttItems(newGanttItems);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, newGanttItems);
      closeImportModal();
    };
    
    const getMonthIndex = (monthId) => GANTT_MONTHS.findIndex(m => m.id === monthId);
    
    const setDevAllocation = (devId, monthId, percentage) => {
      const currentAllocations = devsByMonth[monthId] || {};
      if (percentage === 0 || percentage === '') {
        const newAllocations = { ...currentAllocations }; delete newAllocations[devId];
        setDevsByMonth({ ...devsByMonth, [monthId]: newAllocations });
      } else {
        setDevsByMonth({ ...devsByMonth, [monthId]: { ...currentAllocations, [devId]: parseInt(percentage) || 0 } });
      }
    };
    
    const getDevAllocation = (monthId, devId) => (devsByMonth[monthId] || {})[devId] || 0;
    
    const getModalMonths = () => {
      const startIdx = getMonthIndex(modalStartMonth);
      const endIdx = getMonthIndex(modalEndMonth);
      if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) return [];
      return GANTT_MONTHS.slice(startIdx, endIdx + 1);
    };
    
    const copyDevsToAllMonths = (sourceMonthId) => {
      const sourceDevs = devsByMonth[sourceMonthId] || {};
      const months = getModalMonths();
      const newDevsByMonth = { ...devsByMonth };
      months.forEach(m => { newDevsByMonth[m.id] = { ...sourceDevs }; });
      setDevsByMonth(newDevsByMonth);
    };
    
    const clearAllDevs = () => setDevsByMonth({});
    
    // Get work items for a specific developer
    const getDevWorkItems = (devId) => {
      return ganttItems.filter(item => {
        if (!item.devsByMonth) return false;
        return Object.values(item.devsByMonth).some(monthData => {
          if (Array.isArray(monthData)) return monthData.includes(devId);
          return monthData && typeof monthData === 'object' && monthData[devId] > 0;
        });
      }).map(item => {
        // Find the actual months this dev is assigned
        const assignedMonths = [];
        Object.entries(item.devsByMonth || {}).forEach(([monthId, monthData]) => {
          let allocation = 0;
          if (Array.isArray(monthData) && monthData.includes(devId)) allocation = 100;
          else if (monthData && typeof monthData === 'object' && monthData[devId]) allocation = monthData[devId];
          if (allocation > 0) assignedMonths.push({ monthId, allocation });
        });
        return { ...item, assignedMonths, jiraTicket: extractJiraTicket(item.jiraLink) };
      });
    };
    
    const getDevTotalAllocation = (devId, monthId) => {
      return ganttItems.reduce((total, item) => {
        const monthData = item.devsByMonth?.[monthId];
        if (!monthData) return total;
        if (Array.isArray(monthData)) return total + (monthData.includes(devId) ? 100 : 0);
        return total + (monthData[devId] || 0);
      }, 0);
    };
    
    const saveGanttItem = async () => {
      if (!modalTitle || !modalStartMonth || !modalEndMonth) { alert('Vul alle velden in!'); return; }
      const startIdx = getMonthIndex(modalStartMonth);
      const endIdx = getMonthIndex(modalEndMonth);
      if (endIdx < startIdx) { alert('Eindmaand moet na startmaand liggen!'); return; }
      
      const cleanedDevsByMonth = {};
      Object.entries(devsByMonth).forEach(([monthId, allocations]) => {
        const monthIdx = getMonthIndex(monthId);
        if (monthIdx >= startIdx && monthIdx <= endIdx && allocations && typeof allocations === 'object') {
          const validAllocations = {};
          Object.entries(allocations).forEach(([devId, pct]) => { if (pct > 0) validAllocations[devId] = pct; });
          if (Object.keys(validAllocations).length > 0) cleanedDevsByMonth[monthId] = validAllocations;
        }
      });
      
      let newGanttItems;
      if (editingGanttItem) {
        newGanttItems = ganttItems.map(item => item.id === editingGanttItem.id 
          ? { ...item, title: modalTitle, startMonth: modalStartMonth, endMonth: modalEndMonth, swimlane: modalSwimlane, jiraLink: modalJiraLink, devsByMonth: cleanedDevsByMonth }
          : item);
      } else {
        newGanttItems = [...ganttItems, {
          id: 'gantt_' + Date.now(), title: modalTitle, startMonth: modalStartMonth, endMonth: modalEndMonth,
          swimlane: modalSwimlane, jiraLink: modalJiraLink, devsByMonth: cleanedDevsByMonth
        }];
      }
      setGanttItems(newGanttItems);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, newGanttItems);
      closeGanttModal();
    };
    
    const deleteGanttItem = async (itemId) => {
      if (window.confirm('Weet je zeker dat je dit item wilt verwijderen?')) {
        const newGanttItems = ganttItems.filter(item => item.id !== itemId);
        setGanttItems(newGanttItems);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, newGanttItems);
      }
    };
    
    const handleMouseEnter = (e, item, dev, allocation) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
      setHoveredItem({ ...item, currentDev: dev, currentAllocation: allocation });
    };
    
    const handleMouseLeave = () => setHoveredItem(null);
    
    const importableItems = getImportableItems();
    const swimlaneConfig = (id) => GANTT_SWIMLANES.find(s => s.id === id) || GANTT_SWIMLANES[0];
    
    return (
      <div>
        {/* Team Management Section */}
        <div style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#F8F9FA', borderRadius: '10px', border: '1px solid #ECF0F1' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span>👥</span>
              <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#2C3E50', margin: 0 }}>Development Team ({developers.length})</h4>
            </div>
            <button onClick={() => openDevModal()} className="text-xs" style={{ padding: '4px 10px', borderRadius: '6px', border: '1px dashed #9B59B6', backgroundColor: 'transparent', color: '#9B59B6', cursor: 'pointer', fontWeight: '600' }}>
              <Plus className="w-3 h-3 inline mr-1" /> Teamlid
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {developers.map(dev => {
              const roleInfo = DEVELOPER_ROLES.find(r => r.id === dev.role);
              return (
                <div key={dev.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', backgroundColor: 'white', borderRadius: '16px', border: `1px solid ${dev.color}40` }}>
                  <span style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: dev.color, color: 'white', fontSize: '9px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{dev.name[0]}</span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#2C3E50' }}>{dev.name}</span>
                  <span style={{ fontSize: '9px', color: roleInfo?.color, padding: '1px 4px', backgroundColor: roleInfo?.color + '15', borderRadius: '4px' }}>{roleInfo?.label}</span>
                  <button onClick={() => openDevModal(dev)} style={{ padding: '2px', background: 'none', border: 'none', color: '#BDC3C7', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                  <button onClick={() => deleteDev(dev.id)} style={{ padding: '2px', background: 'none', border: 'none', color: '#BDC3C7', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <p className="text-xs sm:text-sm" style={{ color: '#7F8C8D' }}>Timeline per developer met JIRA tickets en bezetting.</p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={openImportModal} className="text-xs" style={{ padding: '6px 12px', borderRadius: '8px', border: '2px solid #3498DB', backgroundColor: '#EBF5FB', color: '#3498DB', cursor: 'pointer', fontWeight: '600' }}>📥 Import ({importableItems.length})</button>
            <button onClick={() => openGanttModal()} className="text-xs" style={{ padding: '6px 12px', borderRadius: '8px', border: '2px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontWeight: '600' }}><Plus className="w-3 h-3 inline mr-1" /> Nieuw</button>
          </div>
        </div>
        
        {/* Tooltip */}
        {hoveredItem && (
          <div style={{
            position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, transform: 'translate(-50%, -100%)',
            backgroundColor: '#2C3E50', color: 'white', padding: '12px 16px', borderRadius: '8px',
            fontSize: '12px', zIndex: 9999, maxWidth: '300px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '6px' }}>
              {hoveredItem.jiraTicket && <span style={{ color: '#3498DB', marginRight: '8px' }}>{hoveredItem.jiraTicket}</span>}
              {hoveredItem.title}
            </div>
            <div style={{ display: 'grid', gap: '4px', fontSize: '11px' }}>
              <div>👤 <strong>{hoveredItem.currentDev?.name}</strong>: {hoveredItem.currentAllocation}%</div>
              <div>📅 {GANTT_MONTHS.find(m => m.id === hoveredItem.startMonth)?.label} - {GANTT_MONTHS.find(m => m.id === hoveredItem.endMonth)?.label}</div>
              <div>📁 {swimlaneConfig(hoveredItem.swimlane)?.label}</div>
              {hoveredItem.sourcePersona && <div>👥 Persona: {hoveredItem.sourcePersona}</div>}
              {hoveredItem.jiraLink && <div style={{ color: '#3498DB' }}>🔗 {hoveredItem.jiraLink}</div>}
            </div>
            <div style={{ position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)', 
              width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #2C3E50' }} />
          </div>
        )}
        
        {/* Timeline Grid */}
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <div style={{ minWidth: '900px' }}>
            {/* Month Headers */}
            <div style={{ display: 'flex', marginLeft: '140px', marginBottom: '4px' }}>
              {GANTT_MONTHS.map((month, idx) => (
                <div key={month.id} style={{ 
                  flex: 1, textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#2C3E50', 
                  padding: '8px 0', backgroundColor: idx % 3 === 0 ? '#F8F9FA' : 'white', 
                  borderLeft: idx % 3 === 0 ? '2px solid #BDC3C7' : '1px solid #ECF0F1',
                  borderBottom: '2px solid #BDC3C7'
                }}>
                  <div>{month.label}</div>
                  {idx % 3 === 0 && <div style={{ fontSize: '9px', color: '#7F8C8D', fontWeight: 'normal' }}>{month.quarter}</div>}
                </div>
              ))}
            </div>
            
            {/* Developer Rows */}
            {developers.map(dev => {
              const roleInfo = DEVELOPER_ROLES.find(r => r.id === dev.role);
              const devWorkItems = getDevWorkItems(dev.id);
              
              // Group overlapping items into rows
              const itemRows = [];
              devWorkItems.forEach(item => {
                const itemStart = getMonthIndex(item.startMonth);
                const itemEnd = getMonthIndex(item.endMonth);
                let placed = false;
                for (let row of itemRows) {
                  const hasOverlap = row.some(existing => {
                    const existStart = getMonthIndex(existing.startMonth);
                    const existEnd = getMonthIndex(existing.endMonth);
                    return !(itemEnd < existStart || itemStart > existEnd);
                  });
                  if (!hasOverlap) { row.push(item); placed = true; break; }
                }
                if (!placed) itemRows.push([item]);
              });
              
              const rowHeight = Math.max(1, itemRows.length) * 28 + 16;
              
              return (
                <div key={dev.id} style={{ display: 'flex', minHeight: `${rowHeight}px`, borderBottom: '1px solid #ECF0F1' }}>
                  {/* Developer Info */}
                  <div style={{ 
                    width: '140px', flexShrink: 0, padding: '8px', 
                    backgroundColor: dev.color + '15', borderRight: `3px solid ${dev.color}`,
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}>
                    <span style={{ 
                      width: '28px', height: '28px', borderRadius: '50%', backgroundColor: dev.color, 
                      color: 'white', fontSize: '12px', fontWeight: 'bold', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                    }}>{dev.name[0]}</span>
                    <div style={{ overflow: 'hidden' }}>
                      <p style={{ fontWeight: '600', color: '#2C3E50', fontSize: '12px' }}>{dev.name}</p>
                      <p style={{ fontSize: '10px', color: roleInfo?.color }}>{roleInfo?.label}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Area */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    {/* Grid Background */}
                    <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                      {GANTT_MONTHS.map((month, idx) => {
                        const totalAlloc = getDevTotalAllocation(dev.id, month.id);
                        return (
                          <div key={month.id} style={{ 
                            flex: 1, 
                            borderLeft: idx % 3 === 0 ? '2px solid #ECF0F1' : '1px solid #F8F9FA',
                            backgroundColor: totalAlloc > 100 ? '#FEF3E2' : totalAlloc > 0 ? '#F8F9FA' : 'transparent'
                          }} />
                        );
                      })}
                    </div>
                    
                    {/* Work Items */}
                    {itemRows.map((row, rowIdx) => (
                      row.map(item => {
                        const startIdx = getMonthIndex(item.startMonth);
                        const endIdx = getMonthIndex(item.endMonth);
                        const span = endIdx - startIdx + 1;
                        const swimlane = swimlaneConfig(item.swimlane);
                        
                        // Get allocation for this dev in this item
                        const allocations = item.assignedMonths.filter(am => {
                          const mIdx = getMonthIndex(am.monthId);
                          return mIdx >= startIdx && mIdx <= endIdx;
                        });
                        const avgAllocation = allocations.length > 0 
                          ? Math.round(allocations.reduce((sum, a) => sum + a.allocation, 0) / allocations.length) 
                          : 100;
                        
                        return (
                          <div
                            key={item.id}
                            onClick={() => openGanttModal(item)}
                            onMouseEnter={(e) => handleMouseEnter(e, item, dev, avgAllocation)}
                            onMouseLeave={handleMouseLeave}
                            style={{
                              position: 'absolute',
                              top: `${8 + rowIdx * 28}px`,
                              left: `${(startIdx / 12) * 100}%`,
                              width: `${(span / 12) * 100}%`,
                              height: '24px',
                              backgroundColor: swimlane.color,
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0 6px',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '500',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              zIndex: 10,
                              overflow: 'hidden'
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', flex: 1 }}>
                              {item.jiraTicket && (
                                <span style={{ 
                                  backgroundColor: 'rgba(255,255,255,0.25)', 
                                  padding: '1px 4px', 
                                  borderRadius: '3px', 
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  flexShrink: 0
                                }}>{item.jiraTicket}</span>
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.title}
                              </span>
                            </span>
                            <span style={{ 
                              backgroundColor: 'rgba(0,0,0,0.2)', 
                              padding: '1px 4px', 
                              borderRadius: '3px', 
                              fontSize: '9px',
                              marginLeft: '4px',
                              flexShrink: 0
                            }}>{avgAllocation}%</span>
                          </div>
                        );
                      })
                    ))}
                    
                    {/* Empty state */}
                    {devWorkItems.length === 0 && (
                      <div style={{ 
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', 
                        justifyContent: 'center', color: '#BDC3C7', fontSize: '11px' 
                      }}>
                        Geen toegewezen werk
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Summary Row */}
            <div style={{ display: 'flex', marginTop: '8px', backgroundColor: '#2C3E50', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ width: '140px', flexShrink: 0, padding: '10px', color: 'white' }}>
                <p style={{ fontSize: '11px', fontWeight: '600' }}>Totale Bezetting</p>
              </div>
              <div style={{ flex: 1, display: 'flex' }}>
                {GANTT_MONTHS.map((month, idx) => {
                  const totalAlloc = developers.reduce((sum, dev) => sum + getDevTotalAllocation(dev.id, month.id), 0);
                  const avgAlloc = Math.round(totalAlloc / developers.length);
                  const color = avgAlloc > 100 ? '#E67E22' : avgAlloc > 75 ? '#F39C12' : avgAlloc > 50 ? '#27AE60' : '#7F8C8D';
                  return (
                    <div key={month.id} style={{ 
                      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      borderLeft: idx > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none', padding: '6px 0'
                    }}>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', color }}>{avgAlloc}%</span>
                      <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.6)' }}>gem.</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div style={{ marginTop: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#7F8C8D' }}>Categorieën:</span>
          {GANTT_SWIMLANES.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: s.color }} />
              <span style={{ fontSize: '10px', color: '#7F8C8D' }}>{s.label}</span>
            </div>
          ))}
        </div>
        
        {/* Import Modal */}
        {showImportModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeImportModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '16px', borderBottom: '3px solid #3498DB' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50', margin: 0 }}>📥 Importeer naar Timeline</h3>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {importableItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#7F8C8D' }}>
                    <p>✅ Alle roadmap items zijn al geïmporteerd!</p>
                  </div>
                ) : (
                  <div>
                    {QUARTERS.filter(q => q.id !== 'backlog').map(quarter => {
                      const quarterItems = importableItems.filter(item => item.quarter === quarter.id);
                      if (quarterItems.length === 0) return null;
                      return (
                        <div key={quarter.id} style={{ marginBottom: '16px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>📅 {quarter.label}</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {quarterItems.map(item => {
                              const itemKey = `${item.personaId}-${item.id}`;
                              const isSelected = selectedImportItems.includes(itemKey);
                              return (
                                <label key={itemKey} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', backgroundColor: isSelected ? '#EBF5FB' : '#F8F9FA', borderRadius: '8px', cursor: 'pointer', border: isSelected ? '2px solid #3498DB' : '2px solid transparent' }}>
                                  <input type="checkbox" checked={isSelected} onChange={() => toggleImportItem(itemKey)} style={{ marginRight: '12px', width: '18px', height: '18px' }} />
                                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#2C3E50' }}>{item.text}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px', borderTop: '1px solid #ECF0F1', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={closeImportModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={importSelectedItems} disabled={selectedImportItems.length === 0} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: selectedImportItems.length > 0 ? '#3498DB' : '#BDC3C7', color: 'white', cursor: selectedImportItems.length > 0 ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>📥 Importeer ({selectedImportItems.length})</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Gantt Modal */}
        {showGanttModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeGanttModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '750px', padding: '16px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50', margin: 0 }}>{editingGanttItem ? '✏️ Werk Item Bewerken' : '📊 Nieuw Werk Item'}</h3>
                {editingGanttItem && (
                  <button onClick={() => { deleteGanttItem(editingGanttItem.id); closeGanttModal(); }} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#FEF3E2', color: '#E67E22', cursor: 'pointer', fontSize: '12px' }}>🗑️ Verwijderen</button>
                )}
              </div>
              
              {editingGanttItem?.sourceId && (
                <div style={{ padding: '8px 12px', backgroundColor: '#EBF5FB', borderRadius: '6px', marginBottom: '16px', fontSize: '12px', color: '#3498DB' }}>
                  📥 Geïmporteerd van: <strong>{editingGanttItem.sourcePersona}</strong>
                  {editingGanttItem.fte > 0 && <span style={{ marginLeft: '12px' }}>👥 {editingGanttItem.fte} FTE</span>}
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Titel</label>
                  <input value={modalTitle} onChange={(e) => setModalTitle(e.target.value)} placeholder="bijv. API Integratie" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Categorie</label>
                  <select value={modalSwimlane} onChange={(e) => setModalSwimlane(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {GANTT_SWIMLANES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>🔗 JIRA Link</label>
                <input value={modalJiraLink} onChange={(e) => setModalJiraLink(e.target.value)} placeholder="https://jira.company.com/browse/PROJ-123" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
                {modalJiraLink && extractJiraTicket(modalJiraLink) && (
                  <p style={{ fontSize: '11px', color: '#27AE60', marginTop: '4px' }}>✓ Herkend: <strong>{extractJiraTicket(modalJiraLink)}</strong></p>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Start Maand</label>
                  <select value={modalStartMonth} onChange={(e) => setModalStartMonth(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {GANTT_MONTHS.map(m => <option key={m.id} value={m.id}>{m.label} ({m.quarter})</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Eind Maand</label>
                  <select value={modalEndMonth} onChange={(e) => setModalEndMonth(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {GANTT_MONTHS.map(m => <option key={m.id} value={m.id}>{m.label} ({m.quarter})</option>)}
                  </select>
                </div>
              </div>
              
              {/* Resource Planning Matrix */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px', border: '2px solid #9B59B6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#9B59B6' }}>🧑‍💻 Resource Planning per Maand (%)</label>
                  <button type="button" onClick={clearAllDevs} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #BDC3C7', backgroundColor: 'white', color: '#7F8C8D', cursor: 'pointer' }}>🗑️ Wis alles</button>
                </div>
                
                {getModalMonths().length === 0 ? (
                  <p style={{ fontSize: '12px', color: '#E67E22', textAlign: 'center', padding: '12px' }}>⚠️ Selecteer geldige start- en eindmaand</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ECF0F1', width: '100px' }}>Developer</th>
                          {getModalMonths().map(month => (
                            <th key={month.id} style={{ textAlign: 'center', padding: '6px 4px', borderBottom: '2px solid #ECF0F1', minWidth: '55px' }}>
                              <div>{month.label}</div>
                              <div style={{ fontSize: '9px', color: '#BDC3C7', fontWeight: 'normal' }}>{month.quarter}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {developers.map(dev => {
                          const roleInfo = DEVELOPER_ROLES.find(r => r.id === dev.role);
                          return (
                            <tr key={dev.id}>
                              <td style={{ padding: '4px 8px', borderBottom: '1px solid #ECF0F1' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: dev.color, color: 'white', fontSize: '9px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{dev.name[0]}</span>
                                  <div>
                                    <div style={{ fontWeight: '600', fontSize: '11px' }}>{dev.name}</div>
                                    <div style={{ fontSize: '9px', color: roleInfo?.color }}>{roleInfo?.label}</div>
                                  </div>
                                </div>
                              </td>
                              {getModalMonths().map(month => {
                                const currentAllocation = getDevAllocation(month.id, dev.id);
                                return (
                                  <td key={month.id} style={{ textAlign: 'center', padding: '4px 2px', borderBottom: '1px solid #ECF0F1' }}>
                                    <select
                                      value={currentAllocation}
                                      onChange={(e) => setDevAllocation(dev.id, month.id, e.target.value)}
                                      style={{ 
                                        width: '50px', padding: '4px 2px', borderRadius: '4px', 
                                        border: currentAllocation > 0 ? `2px solid ${dev.color}` : '1px solid #ECF0F1',
                                        backgroundColor: currentAllocation > 0 ? dev.color + '20' : 'white',
                                        color: currentAllocation > 0 ? dev.color : '#BDC3C7',
                                        fontSize: '11px', fontWeight: currentAllocation > 0 ? 'bold' : 'normal',
                                        cursor: 'pointer', textAlign: 'center'
                                      }}
                                    >
                                      <option value={0}>-</option>
                                      <option value={5}>5%</option>
                                      <option value={10}>10%</option>
                                      <option value={25}>25%</option>
                                      <option value={50}>50%</option>
                                      <option value={75}>75%</option>
                                      <option value={100}>100%</option>
                                    </select>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid #ECF0F1', paddingTop: '12px' }}>
                      <span style={{ fontSize: '11px', color: '#7F8C8D' }}>Snel kopiëren:</span>
                      {getModalMonths().map(month => {
                        const hasAllocations = Object.keys(devsByMonth[month.id] || {}).length > 0;
                        return hasAllocations && (
                          <button key={month.id} type="button" onClick={() => copyDevsToAllMonths(month.id)} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #9B59B6', backgroundColor: '#F5EEF8', color: '#9B59B6', cursor: 'pointer' }}>
                            📋 {month.label} → alle
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={closeGanttModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={saveGanttItem} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingGanttItem ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Dev Modal */}
        {showDevModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeDevModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '20px' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50', marginBottom: '16px' }}>
                {editingDev ? '✏️ Teamlid Bewerken' : '👤 Nieuw Teamlid'}
              </h3>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Naam *</label>
                <input value={devForm.name} onChange={(e) => setDevForm({...devForm, name: e.target.value})} placeholder="bijv. Jan" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Rol</label>
                <select value={devForm.role} onChange={(e) => setDevForm({...devForm, role: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                  {DEVELOPER_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Kleur</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {DEV_COLORS.map(color => (
                    <button key={color} type="button" onClick={() => setDevForm({...devForm, color})}
                      style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color, border: devForm.color === color ? '3px solid #2C3E50' : '2px solid transparent', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={closeDevModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={saveDev} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#9B59B6', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingDev ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    );
  };

  // ============================================
  // IT COSTS COMPONENT
  // ============================================
  const ITCostsSection = () => {
    const [showCostModal, setShowCostModal] = useState(false);
    const [editingCost, setEditingCost] = useState(null);
    const [costForm, setCostForm] = useState({
      name: '', amount: '', category: 'hosting', entity: 'geriforce_bv', frequency: 'monthly', costType: 'behoud', notes: ''
    });
    
    // Uitgangspunten state
    const [uitgangspunten, setUitgangspunten] = useState(() => {
      const saved = localStorage.getItem('gericall_uitgangspunten_v2');
      return saved ? JSON.parse(saved) : [
        { id: 'u1', icon: '👥', text: 'Team stabiel voor 2026.' },
        { id: 'u2', icon: '➕', text: 'Potentiële uitbreiding: +1 Back-end developer (kandidaat beschikbaar).' },
        { id: 'u3', icon: '🇪🇺', text: 'Tech stack: blijft gelijk met focus op EU-based suppliers.' }
      ];
    });
    const [showUitgangspuntModal, setShowUitgangspuntModal] = useState(false);
    const [editingUitgangspunt, setEditingUitgangspunt] = useState(null);
    const [uitgangspuntForm, setUitgangspuntForm] = useState({ icon: '📌', text: '' });
    
    const UITGANGSPUNT_ICONS = ['📌', '👥', '➕', '🇪🇺', '💡', '🎯', '⚡', '🔒', '💰', '📊', '🚀', '⏰', '✅', '⚠️', '🔧', '📈', '🏢', '🌐', '📱', '💻'];
    
    const saveUitgangspuntenToStorage = (items) => {
      localStorage.setItem('gericall_uitgangspunten_v2', JSON.stringify(items));
    };
    
    const openUitgangspuntModal = (item = null) => {
      if (item) {
        setEditingUitgangspunt(item);
        setUitgangspuntForm({ icon: item.icon || '📌', text: item.text || '' });
      } else {
        setEditingUitgangspunt(null);
        setUitgangspuntForm({ icon: '📌', text: '' });
      }
      setShowUitgangspuntModal(true);
    };
    
    const closeUitgangspuntModal = () => {
      setShowUitgangspuntModal(false);
      setEditingUitgangspunt(null);
      setUitgangspuntForm({ icon: '📌', text: '' });
    };
    
    const saveUitgangspunt = () => {
      if (!uitgangspuntForm.text.trim()) { alert('Vul een tekst in!'); return; }
      let newItems;
      if (editingUitgangspunt) {
        newItems = uitgangspunten.map(u => u.id === editingUitgangspunt.id ? { ...u, ...uitgangspuntForm } : u);
      } else {
        newItems = [...uitgangspunten, { id: 'u_' + Date.now(), ...uitgangspuntForm }];
      }
      setUitgangspunten(newItems);
      saveUitgangspuntenToStorage(newItems);
      closeUitgangspuntModal();
    };
    
    const deleteUitgangspunt = (id) => {
      if (window.confirm('Weet je zeker dat je dit uitgangspunt wilt verwijderen?')) {
        const newItems = uitgangspunten.filter(u => u.id !== id);
        setUitgangspunten(newItems);
        saveUitgangspuntenToStorage(newItems);
      }
    };
    
    const moveUitgangspunt = (id, direction) => {
      const idx = uitgangspunten.findIndex(u => u.id === id);
      if (idx === -1) return;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= uitgangspunten.length) return;
      const newItems = [...uitgangspunten];
      [newItems[idx], newItems[newIdx]] = [newItems[newIdx], newItems[idx]];
      setUitgangspunten(newItems);
      saveUitgangspuntenToStorage(newItems);
    };
    
    const openCostModal = (cost = null) => {
      if (cost) {
        setEditingCost(cost);
        setCostForm({
          name: cost.name || '',
          amount: cost.amount || '',
          category: cost.category || 'hosting',
          entity: cost.entity || 'geriforce_bv',
          frequency: cost.frequency || 'monthly',
          costType: cost.costType || 'behoud',
          notes: cost.notes || ''
        });
      } else {
        setEditingCost(null);
        setCostForm({ name: '', amount: '', category: 'hosting', entity: 'geriforce_bv', frequency: 'monthly', costType: 'behoud', notes: '' });
      }
      setShowCostModal(true);
    };
    
    const closeCostModal = () => {
      setShowCostModal(false);
      setEditingCost(null);
      setCostForm({ name: '', amount: '', category: 'hosting', entity: 'geriforce_bv', frequency: 'monthly', costType: 'behoud', notes: '' });
    };
    
    const saveCost = async () => {
      if (!costForm.name || !costForm.amount) { alert('Vul naam en bedrag in!'); return; }
      
      let newCosts;
      if (editingCost) {
        newCosts = itCosts.map(c => c.id === editingCost.id ? { ...c, ...costForm, amount: parseFloat(costForm.amount) } : c);
      } else {
        newCosts = [...itCosts, { id: 'cost_' + Date.now(), ...costForm, amount: parseFloat(costForm.amount) }];
      }
      setItCosts(newCosts);
      await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, ganttItems, developers, newCosts);
      closeCostModal();
    };
    
    const deleteCost = async (costId) => {
      if (window.confirm('Weet je zeker dat je deze kostenpost wilt verwijderen?')) {
        const newCosts = itCosts.filter(c => c.id !== costId);
        setItCosts(newCosts);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, ganttItems, developers, newCosts);
      }
    };
    
    const getYearlyCost = (cost) => {
      const freq = COST_FREQUENCIES.find(f => f.id === cost.frequency);
      return cost.amount * (freq?.multiplier || 1);
    };
    
    const totalYearlyCost = itCosts.reduce((sum, c) => sum + getYearlyCost(c), 0);
    const revenuePercentage = ((totalYearlyCost / REVENUE_2026) * 100).toFixed(1);
    
    // Calculations by cost type
    const costsByType = COST_TYPES.map(type => ({
      ...type,
      costs: itCosts.filter(c => (c.costType || 'behoud') === type.id),
      total: itCosts.filter(c => (c.costType || 'behoud') === type.id).reduce((sum, c) => sum + getYearlyCost(c), 0)
    }));
    
    const costsByCategory = COST_CATEGORIES.map(cat => ({
      ...cat,
      total: itCosts.filter(c => c.category === cat.id).reduce((sum, c) => sum + getYearlyCost(c), 0)
    })).filter(c => c.total > 0);
    
    const formatCurrency = (amount) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
    const formatPercentage = (amount) => ((amount / REVENUE_2026) * 100).toFixed(1);
    
    const resetToDefaults = async () => {
      if (window.confirm('Weet je zeker dat je alle kosten wilt resetten naar de standaardwaarden? Dit overschrijft alle aanpassingen.')) {
        const newCosts = [...DEFAULT_IT_COSTS];
        setItCosts(newCosts);
        await saveToCloud(personas, comments, journeyStages, journeyBundles, strategicVisions, businessVisions, journeys, ganttItems, developers, newCosts);
        alert('✅ ' + DEFAULT_IT_COSTS.length + ' kostenposten geladen!');
      }
    };
    
    return (
      <div>
        {/* Uitgangspunten Section */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#F8F9FA', borderRadius: '12px', border: '2px solid #3498DB' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '20px' }}>📋</span>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#2C3E50', margin: 0 }}>Uitgangspunten IT 2026</h4>
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EBF5FB', color: '#3498DB' }}>{uitgangspunten.length} items</span>
            </div>
            <button onClick={() => openUitgangspuntModal()} className="text-xs" style={{ padding: '6px 12px', borderRadius: '6px', border: '2px dashed #3498DB', backgroundColor: 'transparent', color: '#3498DB', cursor: 'pointer', fontWeight: '600' }}>
              <Plus className="w-3 h-3 inline mr-1" /> Toevoegen
            </button>
          </div>
          
          {uitgangspunten.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#BDC3C7' }}>
              <p style={{ fontSize: '13px' }}>Nog geen uitgangspunten toegevoegd</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {uitgangspunten.map((item, idx) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: 'white', borderRadius: '8px', borderLeft: '4px solid #3498DB' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                  <p style={{ flex: 1, fontSize: '13px', color: '#2C3E50', margin: 0, lineHeight: '1.5' }}>{item.text}</p>
                  <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                    {idx > 0 && (
                      <button onClick={() => moveUitgangspunt(item.id, 'up')} title="Omhoog" style={{ padding: '4px', background: 'none', border: 'none', color: '#BDC3C7', cursor: 'pointer', fontSize: '10px' }}>▲</button>
                    )}
                    {idx < uitgangspunten.length - 1 && (
                      <button onClick={() => moveUitgangspunt(item.id, 'down')} title="Omlaag" style={{ padding: '4px', background: 'none', border: 'none', color: '#BDC3C7', cursor: 'pointer', fontSize: '10px' }}>▼</button>
                    )}
                    <button onClick={() => openUitgangspuntModal(item)} title="Bewerken" style={{ padding: '4px', background: 'none', border: 'none', color: '#3498DB', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                    <button onClick={() => deleteUitgangspunt(item.id)} title="Verwijderen" style={{ padding: '4px', background: 'none', border: 'none', color: '#BDC3C7', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Header - IT Cost vs Revenue */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #ECF0F1' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span style={{ fontSize: '16px' }}>💰</span>
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50', margin: 0 }}>IT Budget 2026</h3>
                <p style={{ fontSize: '11px', color: '#7F8C8D', margin: 0 }}>Omzetprognose: {formatCurrency(REVENUE_2026)}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, color: '#2C3E50' }}>{formatCurrency(totalYearlyCost)}</p>
              <p style={{ fontSize: '12px', color: '#FF8C42', fontWeight: '600', margin: 0 }}>{revenuePercentage}% van omzet</p>
            </div>
          </div>
          
          {/* Cost Type Split - inline */}
          <div style={{ display: 'flex', gap: '12px', paddingTop: '12px', borderTop: '1px solid #ECF0F1' }}>
            {costsByType.map(type => (
              <div key={type.id} style={{ flex: 1, padding: '10px 12px', backgroundColor: '#F8F9FA', borderRadius: '8px', borderLeft: `3px solid ${type.color}` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: '12px' }}>{type.icon}</span>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#2C3E50' }}>{type.label}</span>
                  </div>
                  <span style={{ fontSize: '10px', color: '#7F8C8D' }}>{type.costs.length} posten</span>
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: type.color }}>{formatCurrency(type.total)}</span>
                  <span style={{ fontSize: '11px', color: '#7F8C8D' }}>{formatPercentage(type.total)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cost Items by Type */}
        <div className="flex justify-between items-center mb-4">
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50' }}>📊 Alle Kostenposten ({itCosts.length})</h4>
          <div className="flex gap-2">
            <button onClick={resetToDefaults} className="text-xs" style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #BDC3C7', backgroundColor: 'white', color: '#7F8C8D', cursor: 'pointer' }}>
              🔄 Reset defaults
            </button>
            <button onClick={() => openCostModal()} className="text-xs" style={{ padding: '6px 12px', borderRadius: '8px', border: '2px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontWeight: '600' }}>
              <Plus className="w-3 h-3 inline mr-1" /> Nieuwe Kost
            </button>
          </div>
        </div>
        
        {/* Costs grouped by Type - 3 columns */}
        {itCosts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#F8F9FA', borderRadius: '12px', border: '2px dashed #BDC3C7' }}>
            <p style={{ fontSize: '16px', color: '#7F8C8D', marginBottom: '12px' }}>💰 Geen kostenposten gevonden</p>
            <p style={{ fontSize: '13px', color: '#BDC3C7', marginBottom: '16px' }}>De cloud data bevat geen kosten. Klik hieronder om de standaard kostenposten te laden.</p>
            <button onClick={resetToDefaults} style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
              🔄 Laad Standaard Kostenposten ({DEFAULT_IT_COSTS.length} items)
            </button>
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', alignItems: 'start' }}>
          {costsByType.map(type => (
            <div key={type.id} style={{ backgroundColor: 'white', borderRadius: '12px', border: `2px solid ${type.color}40`, overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', backgroundColor: type.color + '15', borderBottom: `2px solid ${type.color}40`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{type.icon}</span>
                  <span style={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '13px' }}>{type.label}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontWeight: 'bold', color: type.color, fontSize: '12px' }}>{formatCurrency(type.total)}</span>
                  <span style={{ fontSize: '10px', color: '#7F8C8D', display: 'block' }}>{formatPercentage(type.total)}%</span>
                </div>
              </div>
              <div style={{ padding: '6px' }}>
                {type.costs.length === 0 ? (
                  <p style={{ fontSize: '11px', color: '#BDC3C7', textAlign: 'center', padding: '16px 8px' }}>Geen posten</p>
                ) : (
                  type.costs.map(cost => {
                    const category = COST_CATEGORIES.find(c => c.id === cost.category);
                    const frequency = COST_FREQUENCIES.find(f => f.id === cost.frequency);
                    const yearlyCost = getYearlyCost(cost);
                    return (
                      <div key={cost.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', borderRadius: '6px', marginBottom: '4px', backgroundColor: '#F8F9FA' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <span style={{ fontSize: '12px' }}>{category?.icon}</span>
                            <p style={{ fontWeight: '600', color: '#2C3E50', fontSize: '11px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cost.name}</p>
                          </div>
                          <p style={{ fontSize: '9px', color: '#7F8C8D', margin: 0 }}>
                            {frequency?.label} {cost.notes && <span>• {cost.notes.length > 20 ? cost.notes.substring(0, 20) + '...' : cost.notes}</span>}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ fontWeight: 'bold', color: '#2C3E50', fontSize: '11px', margin: 0 }}>{formatCurrency(yearlyCost)}</p>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <button onClick={() => openCostModal(cost)} style={{ padding: '2px', borderRadius: '3px', border: 'none', backgroundColor: 'transparent', color: '#3498DB', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                            <button onClick={() => deleteCost(cost.id)} style={{ padding: '2px', borderRadius: '3px', border: 'none', backgroundColor: 'transparent', color: '#BDC3C7', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
        )}
        
        {/* Category breakdown footer */}
        {itCosts.length > 0 && (
          <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#F8F9FA', borderRadius: '12px', border: '1px solid #ECF0F1' }}>
            <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#2C3E50', marginBottom: '12px' }}>📈 Uitsplitsing per Categorie</h5>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {costsByCategory.map(cat => (
                <div key={cat.id} style={{ padding: '8px 12px', backgroundColor: 'white', borderRadius: '8px', borderLeft: `3px solid ${cat.color}`, minWidth: '140px' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span>{cat.icon}</span>
                    <span style={{ fontSize: '11px', color: '#7F8C8D' }}>{cat.label}</span>
                  </div>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#2C3E50', margin: 0 }}>{formatCurrency(cat.total)}</p>
                  <p style={{ fontSize: '10px', color: '#7F8C8D', margin: 0 }}>{formatPercentage(cat.total)}% van omzet</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Cost Modal */}
        {showCostModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeCostModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '550px', padding: '20px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50', marginBottom: '16px' }}>
                {editingCost ? '✏️ Kostenpost Bewerken' : '💰 Nieuwe Kostenpost'}
              </h3>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Naam *</label>
                <input value={costForm.name} onChange={(e) => setCostForm({...costForm, name: e.target.value})} placeholder="bijv. Azure Hosting" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
              </div>
              
              <div className="grid grid-cols-2 gap-3" style={{ marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Bedrag (€) *</label>
                  <input type="number" step="0.01" value={costForm.amount} onChange={(e) => setCostForm({...costForm, amount: e.target.value})} placeholder="4000" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Frequentie</label>
                  <select value={costForm.frequency} onChange={(e) => setCostForm({...costForm, frequency: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {COST_FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              
              {/* Cost Type Selection */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Type Investering</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {COST_TYPES.map(type => (
                    <button key={type.id} type="button" onClick={() => setCostForm({...costForm, costType: type.id})}
                      style={{ 
                        padding: '12px 8px', borderRadius: '8px', 
                        border: costForm.costType === type.id ? `2px solid ${type.color}` : '2px solid #ECF0F1',
                        backgroundColor: costForm.costType === type.id ? type.color + '20' : 'white',
                        cursor: 'pointer', textAlign: 'center'
                      }}>
                      <span style={{ fontSize: '18px', display: 'block', marginBottom: '4px' }}>{type.icon}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: costForm.costType === type.id ? type.color : '#7F8C8D' }}>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3" style={{ marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Categorie</label>
                  <select value={costForm.category} onChange={(e) => setCostForm({...costForm, category: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {COST_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Betaald door</label>
                  <select value={costForm.entity} onChange={(e) => setCostForm({...costForm, entity: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                    {COST_ENTITIES.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Notities</label>
                <input value={costForm.notes} onChange={(e) => setCostForm({...costForm, notes: e.target.value})} placeholder="bijv. incl. backup & disaster recovery" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
              </div>
              
              {costForm.amount && (
                <div style={{ padding: '12px', backgroundColor: '#E8F8F0', borderRadius: '8px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '12px', color: '#27AE60', margin: 0 }}>
                    💡 Jaarlijkse kosten: <strong>{formatCurrency(parseFloat(costForm.amount) * (COST_FREQUENCIES.find(f => f.id === costForm.frequency)?.multiplier || 1))}</strong>
                    <span style={{ marginLeft: '8px', color: '#7F8C8D' }}>
                      ({formatPercentage(parseFloat(costForm.amount) * (COST_FREQUENCIES.find(f => f.id === costForm.frequency)?.multiplier || 1))}% van omzet)
                    </span>
                  </p>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={closeCostModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={saveCost} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingCost ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Uitgangspunt Modal */}
        {showUitgangspuntModal && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeUitgangspuntModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '500px', padding: '20px' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50', marginBottom: '16px' }}>
                {editingUitgangspunt ? '✏️ Uitgangspunt Bewerken' : '📋 Nieuw Uitgangspunt'}
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Icoon</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px' }}>
                  {UITGANGSPUNT_ICONS.map(icon => (
                    <button key={icon} type="button" onClick={() => setUitgangspuntForm({...uitgangspuntForm, icon})}
                      style={{ width: '36px', height: '36px', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                        border: uitgangspuntForm.icon === icon ? '2px solid #3498DB' : '2px solid transparent', 
                        backgroundColor: uitgangspuntForm.icon === icon ? '#EBF5FB' : 'white', cursor: 'pointer' }}>
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Tekst *</label>
                <textarea 
                  value={uitgangspuntForm.text} 
                  onChange={(e) => setUitgangspuntForm({...uitgangspuntForm, text: e.target.value})}
                  placeholder="bijv. Focus op stabiliteit en security updates"
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', resize: 'vertical' }} 
                />
              </div>
              
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px', borderLeft: '4px solid #3498DB' }}>
                <p style={{ fontSize: '11px', color: '#7F8C8D', marginBottom: '6px' }}>Preview:</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>{uitgangspuntForm.icon}</span>
                  <span style={{ fontSize: '13px', color: '#2C3E50' }}>{uitgangspuntForm.text || 'Jouw uitgangspunt...'}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={closeUitgangspuntModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button onClick={saveUitgangspunt} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#3498DB', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingUitgangspunt ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // PERSONA JOURNEY VIEW
  // ============================================
  const PersonaJourneyView = () => {
    const PersonaCard = ({ persona }) => {
      const [isHovered, setIsHovered] = useState(false);
      if (!persona) return null;
      const isSelected = selectedPersona?.id === persona.id;
      const hasMockups = persona.mockups && persona.mockups.length > 0;
      const personaComments = comments[persona.id] || [];
      const hasComments = personaComments.length > 0;
      
      // Count by status
      const statusCounts = {
        development: personaComments.filter(c => c.status === 'development').length,
        ready: personaComments.filter(c => c.status === 'ready').length,
        draft: personaComments.filter(c => !c.status || c.status === 'draft').length
      };
      
      const getPersonIcon = () => {
        if (persona.icon) return getPersonaIcon(persona.icon, 5);
        switch(persona.id) {
          case 'zib': return <Building2 className="w-5 h-5" />;
          case 'zim': return <BarChart3 className="w-5 h-5" />;
          case 'tm': return <ClipboardCheck className="w-5 h-5" />;
          case 'vz': return <User className="w-5 h-5" />;
          case 'pl': return <Calendar className="w-5 h-5" />;
          case 'va': return <Phone className="w-5 h-5" />;
          case 'aa': return <Stethoscope className="w-5 h-5" />;
          case 'sp': return <Brain className="w-5 h-5" />;
          default: return <User className="w-5 h-5" />;
        }
      };
      
      return (
        <div style={{ position: 'relative' }}>
          {hasComments && (
            <div style={{ position: 'absolute', top: '-6px', right: '-6px', display: 'flex', gap: '2px', zIndex: 10 }}>
              {statusCounts.development > 0 && (
                <div style={{ backgroundColor: '#27AE60', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: 'white', border: '2px solid white' }}>
                  {statusCounts.development}
                </div>
              )}
              {statusCounts.ready > 0 && (
                <div style={{ backgroundColor: '#3498DB', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: 'white', border: '2px solid white' }}>
                  {statusCounts.ready}
                </div>
              )}
              {statusCounts.draft > 0 && (
                <div style={{ backgroundColor: '#F1C40F', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: '#2C3E50', border: '2px solid white' }}>
                  {statusCounts.draft}
                </div>
              )}
            </div>
          )}
          <button onClick={() => setSelectedPersona(persona)} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
            className="w-full p-3 sm:p-4 text-left transition-all"
            style={{ backgroundColor: isSelected ? '#FFE5D9' : isHovered ? '#F8F9FA' : 'white', borderLeft: `4px solid ${isSelected ? '#FF8C42' : isHovered ? '#BDC3C7' : '#F8F9FA'}`,
              borderRadius: '12px', transform: isSelected ? 'scale(1.02)' : 'scale(1)', cursor: 'pointer' }}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center" style={{ backgroundColor: isSelected ? '#FF8C42' : '#ECF0F1',
                  color: isSelected ? 'white' : '#BDC3C7', borderRadius: '10px' }}>{getPersonIcon()}</div>
                <div>
                  <p className="text-sm sm:text-base font-bold" style={{ color: isSelected ? '#2C3E50' : '#7F8C8D' }}>{persona.naam}</p>
                  <p className="text-xs" style={{ color: '#BDC3C7' }}>{persona.organisatie}</p>
                </div>
              </div>
              {hasMockups && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: isSelected ? '#FF8C42' : '#BDC3C7' }} />}
            </div>
            <p className="text-xs sm:text-sm mb-2 line-clamp-2" style={{ color: '#7F8C8D' }}>{persona.beschrijving}</p>
            <span className="text-xs font-semibold" style={{ color: isSelected ? '#FF8C42' : '#BDC3C7' }}>{persona.rol}</span>
          </button>
        </div>
      );
    };

    const DetailPanel = ({ persona }) => {
      if (!persona) {
        return (
          <div className="flex items-center justify-center h-full p-4" style={{ color: '#BDC3C7' }}>
            <div className="text-center">
              <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4" style={{ color: '#ECF0F1' }} />
              <p className="text-base sm:text-lg font-bold mb-2">Selecteer een persona</p>
            </div>
          </div>
        );
      }
      const personaKey = Object.keys(personas).find(key => personas[key].id === persona.id);
      const hasMockups = persona.mockups && persona.mockups.length > 0;
      
      return (
        <div className="h-full overflow-y-auto p-4 sm:p-6">
          <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FF8C42', color: 'white', borderRadius: '12px' }}>
              {persona.icon ? getPersonaIcon(persona.icon, 6) : <User className="w-6 h-6 sm:w-8 sm:h-8" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h2 className="text-lg sm:text-2xl font-bold" style={{ color: '#2C3E50' }}>{persona.naam}</h2>
                <div className="flex gap-1">
                  <button onClick={() => personaKey && startEditingPersona(persona, personaKey)} style={{ padding: '4px', borderRadius: '6px', backgroundColor: 'transparent', color: '#BDC3C7', border: 'none', cursor: 'pointer' }}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => openCommentModal(persona.id)} title="Roadmap verzoek toevoegen" style={{ padding: '4px', borderRadius: '6px', backgroundColor: 'transparent', color: '#BDC3C7', border: 'none', cursor: 'pointer' }}>🗺️</button>
                  <button onClick={() => personaKey && deletePersona(personaKey)} style={{ padding: '4px', borderRadius: '6px', backgroundColor: 'transparent', color: '#BDC3C7', border: 'none', cursor: 'pointer' }}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm mb-2" style={{ color: '#7F8C8D' }}>{persona.beschrijving}</p>
              <div className="flex items-center flex-wrap gap-2 text-xs sm:text-sm">
                <span className="px-2 sm:px-3 py-1 font-semibold" style={{ backgroundColor: '#FFE5D9', color: '#FF8C42', borderRadius: '6px' }}>{persona.rol}</span>
                <span style={{ color: '#7F8C8D' }}><strong>Freq:</strong> {persona.frequentie}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-bold mb-3 flex items-center space-x-2" style={{ color: '#2C3E50' }}><AlertCircle className="w-5 h-5" style={{ color: '#FF8C42' }} /><span>Uitdagingen & Behoeften</span></h3>
              <div className="mb-4">
                <p className="text-xs font-semibold mb-2" style={{ color: '#7F8C8D' }}>Uitdagingen:</p>
                <ul className="space-y-1">{persona.uitdagingen?.map((u, i) => (<li key={i} className="text-sm" style={{ color: '#7F8C8D' }}>• {u}</li>))}</ul>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: '#7F8C8D' }}>Behoeften:</p>
                <ul className="space-y-1">{persona.behoeften?.map((b, i) => (<li key={i} className="text-sm" style={{ color: '#7F8C8D' }}>✓ {b}</li>))}</ul>
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-3 flex items-center space-x-2" style={{ color: '#2C3E50' }}><TrendingUp className="w-5 h-5" style={{ color: '#FF8C42' }} /><span>KPI's</span></h3>
              <div className="grid grid-cols-2 gap-2">{persona.kpis?.map((k, i) => (<div key={i} className="p-2 text-sm" style={{ backgroundColor: '#F8F9FA', borderRadius: '6px', color: '#2C3E50' }}>{k}</div>))}</div>
            </div>

            {hasMockups && (
              <div>
                <h3 className="font-bold mb-3 flex items-center space-x-2" style={{ color: '#2C3E50' }}><CheckCircle className="w-5 h-5" style={{ color: '#FF8C42' }} /><span>Schermen</span></h3>
                <div className="space-y-2">
                  {persona.mockups.map((m, i) => {
                    const name = m === 'arts-dashboard' ? 'Arts Dashboard' : m === 'arts-mobile' ? 'Arts Mobile App' : m === 'intake-app' ? 'Intake App (NTS)' : m;
                    return (
                      <button key={i} onClick={() => navigateTo(m)} className="w-full p-3 flex items-center justify-between"
                        style={{ backgroundColor: '#FFE5D9', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                        <span className="font-semibold" style={{ color: '#2C3E50' }}>{name}</span>
                        <ExternalLink className="w-5 h-5" style={{ color: '#FF8C42' }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {persona.toekomstigScherm && (
              <div className="p-4" style={{ backgroundColor: '#ECF0F1', borderRadius: '8px' }}>
                <p className="text-sm font-semibold" style={{ color: '#7F8C8D' }}>🔨 Toekomstig: {persona.toekomstigScherm}</p>
              </div>
            )}

            {/* Roadmap Items for this Persona */}
            <div style={{ borderRadius: '12px', padding: '16px', backgroundColor: '#F8F9FA', border: '2px solid #ECF0F1' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold" style={{ color: '#2C3E50' }}>🗺️ Roadmap Items ({(comments[persona.id] || []).length})</h3>
                <button onClick={() => openCommentModal(persona.id)} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontSize: '12px' }}>+ Toevoegen</button>
              </div>
              
              {comments[persona.id]?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(comments[persona.id] || []).map(c => {
                    const status = ROADMAP_STATUSES.find(s => s.id === (c.status || 'draft')) || ROADMAP_STATUSES[0];
                    const quarter = QUARTERS.find(q => q.id === (c.quarter || 'backlog'));
                    const isDraft = (c.status || 'draft') === 'draft';
                    const checksComplete = c.checkAnalyse && c.checkImpact && c.checkMT;
                    
                    return (
                      <div 
                        key={c.id} 
                        className="p-3 bg-white" 
                        style={{ borderLeft: `4px solid ${status.color}`, borderRadius: '6px', cursor: c.jiraLink ? 'pointer' : 'default' }}
                        onClick={() => c.jiraLink && window.open(c.jiraLink, '_blank')}
                        title={c.jiraLink ? `Klik om JIRA te openen: ${c.jiraLink}` : ''}
                      >
                        <div className="flex justify-between items-start">
                          <div style={{ flex: 1 }}>
                            <p className="text-sm font-semibold" style={{ color: '#2C3E50', marginBottom: '4px' }}>{c.text}</p>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '8px', backgroundColor: status.bgColor, color: status.color, fontWeight: '600' }}>
                                {status.icon} {status.label}
                              </span>
                              <span style={{ fontSize: '10px', color: '#7F8C8D' }}>📅 {quarter?.label}</span>
                              {c.fte > 0 && <span style={{ fontSize: '10px', color: '#7F8C8D' }}>👥 {c.fte} FTE</span>}
                              {c.jiraLink && <span style={{ fontSize: '10px', color: '#3498DB' }}>🔗 JIRA</span>}
                              {isDraft && checksComplete && <span style={{ fontSize: '10px' }} title="Alle checks compleet!">🎯</span>}
                            </div>
                            
                            {/* Draft Checks */}
                            {isDraft && (
                              <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                                {DRAFT_CHECKS.map(check => (
                                  <button
                                    key={check.id}
                                    onClick={(e) => { e.stopPropagation(); toggleCommentCheck(persona.id, c.id, check.id); }}
                                    title={`${check.label}: ${c[check.id] ? 'Ja' : 'Nee'}`}
                                    style={{
                                      fontSize: '9px',
                                      padding: '3px 6px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      backgroundColor: c[check.id] ? '#27AE60' : '#ECF0F1',
                                      color: c[check.id] ? 'white' : '#BDC3C7',
                                      cursor: 'pointer',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {c[check.id] ? '✓' : '○'} {check.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <select 
                              value={c.status || 'draft'} 
                              onChange={(e) => { e.stopPropagation(); updateCommentStatus(persona.id, c.id, e.target.value); }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontSize: '10px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #BDC3C7', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                              {ROADMAP_STATUSES.map(s => (
                                <option key={s.id} value={s.id}>{s.icon}</option>
                              ))}
                            </select>
                            <button onClick={(e) => { e.stopPropagation(); openCommentModal(persona.id, c); }} style={{ color: '#3498DB', background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Bewerken"><Edit3 className="w-3 h-3" /></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteComment(persona.id, c.id); }} style={{ color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }} title="Verwijderen"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-center" style={{ color: '#7F8C8D', padding: '16px 0' }}>
                  Nog geen roadmap items voor deze persona.<br/>
                  <span style={{ fontSize: '11px' }}>Items verschijnen ook in het globale overzicht hierboven.</span>
                </p>
              )}
            </div>
          </div>
        </div>
      );
    };

    // Calculate bundle positions - now handled inside journey rendering

    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
        <header className="bg-white" style={{ borderBottom: '3px solid #FF8C42' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '12px', padding: '8px' }}>
                  <img src={logo} alt="GeriCall Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold" style={{ color: '#2C3E50' }}>GeriCall Roadmap 2026</h1>
                  <p className="text-xs sm:text-sm" style={{ color: '#7F8C8D' }}>
                    Zorg van de hoogste kwaliteit, wanneer het er het meest toe doet.
                    {userName && <span className="hidden sm:inline" style={{ marginLeft: '12px', color: '#FF8C42' }}>👤 {userName}</span>}
                    {userRole === 'admin' && <span className="hidden sm:inline" style={{ marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', backgroundColor: '#9B59B6', color: 'white', fontSize: '10px' }}>ADMIN</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-4">
                <span className="hidden sm:inline" style={{ fontSize: '11px', color: '#BDC3C7' }}>{APP_VERSION}</span>
                {lastSync && <span className="hidden sm:inline" style={{ fontSize: '12px', color: '#7F8C8D' }}>✅ {lastSync}</span>}
                <button 
					onClick={() => {
					  const allExpanded = Object.values(expandedSections).every(v => v);
					  setExpandedSections({
						speerpunten: !allExpanded,
						roadmapKwartaal: !allExpanded,
						epics: !allExpanded,
						journeys: !allExpanded,
						personas: !allExpanded,
						gantt: !allExpanded,
						costs: !allExpanded
					  });
					}}
                  className="text-xs sm:text-sm"
                  style={{ backgroundColor: '#ECF0F1', color: '#7F8C8D', borderRadius: '8px', border: 'none', padding: '6px 10px', cursor: 'pointer' }}
                >
                  {Object.values(expandedSections).every(v => v) ? '🔼' : '🔽'} <span className="hidden sm:inline">{Object.values(expandedSections).every(v => v) ? 'Invouwen' : 'Uitvouwen'}</span>
                </button>
                <button onClick={handleLogout} className="text-xs sm:text-sm" style={{ backgroundColor: '#ECF0F1', color: '#7F8C8D', borderRadius: '8px', border: 'none', padding: '6px 10px', cursor: 'pointer' }}>Uitloggen</button>
              </div>
            </div>
          </div>
        </header>

        <div className="px-4 sm:px-8 py-4 sm:py-6">
          {/* ACCORDION 1: Strategic Visions Section */}
          <AccordionSection
            id="speerpunten"
            title="1. Strategische Speerpunten 2026"
            icon="🎯"
            isExpanded={expandedSections.speerpunten}
            onToggle={toggleSection}
            badge={`${businessVisions.length + strategicVisions.length} speerpunten`}
          >
            {/* Central Question */}
            <div className="text-center mb-4 py-3 sm:py-4 px-4 sm:px-6 bg-white" style={{ borderRadius: '12px', borderLeft: '4px solid #FF8C42', borderRight: '4px solid #3498DB' }}>
              <p className="text-sm sm:text-lg font-bold" style={{ color: '#2C3E50' }}>💡 "Waarin moeten we investeren in 2026?"</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Business Personen */}
            <div className="bg-white" style={{ borderRadius: '12px', padding: '16px' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xl sm:text-2xl">🎯</span>
                  <h2 className="text-sm sm:text-lg font-bold" style={{ color: '#2C3E50' }}>Business Speerpunten</h2>
                </div>
                <button onClick={() => openVisionModal(null, 'business')} className="text-xs" style={{ padding: '6px 10px', borderRadius: '8px', border: '2px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontWeight: '600' }}>
                  <Plus className="w-3 h-3 inline" /> <span className="hidden sm:inline ml-1">Toevoegen</span>
                </button>
              </div>
              
              {businessVisions.length === 0 ? (
                <div className="text-center py-6" style={{ color: '#BDC3C7' }}>
                  <p className="text-sm">Nog geen Business speerpunten</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {businessVisions.map((vision, idx) => {
                    const colors = ['#FF8C42', '#E67E22', '#D35400', '#F39C12'];
                    const bgColors = ['#FFE5D9', '#FDEBD0', '#FAE5D3', '#FCF3CF'];
                    const colorIdx = idx % colors.length;
                    const VisionIcon = PERSONA_ICONS.find(i => i.id === (vision.icon || 'user'))?.icon || User;
                    return (
                      <div key={vision.id} style={{ backgroundColor: bgColors[colorIdx], borderRadius: '12px', padding: '12px', position: 'relative', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{ position: 'absolute', top: '6px', right: '6px', display: 'flex', gap: '4px' }}>
                          <button onClick={() => openVisionModal(vision, 'business')} style={{ padding: '4px', borderRadius: '4px', border: 'none', backgroundColor: 'rgba(255,255,255,0.7)', color: '#7F8C8D', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                          <button onClick={() => deleteVision(vision.id, 'business')} style={{ padding: '4px', borderRadius: '4px', border: 'none', backgroundColor: 'rgba(255,255,255,0.7)', color: '#7F8C8D', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                        </div>
                        <div className="hidden sm:flex" style={{ flexDirection: 'column', alignItems: 'center', minWidth: '60px', flexShrink: 0 }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: colors[colorIdx], color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                            <VisionIcon className="w-5 h-5" />
                          </div>
                          <h3 style={{ fontSize: '11px', fontWeight: 'bold', color: '#2C3E50', textAlign: 'center' }}>{vision.naam}</h3>
                        </div>
                        <div style={{ flex: 1, paddingTop: '4px', paddingRight: '16px' }}>
                          <h3 className="sm:hidden text-xs font-bold mb-1" style={{ color: '#2C3E50' }}>{vision.naam}</h3>
                          <p className="text-xs" style={{ color: '#2C3E50', lineHeight: '1.4', fontStyle: 'italic' }}>{vision.quote}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* IT Personen */}
            <div className="bg-white" style={{ borderRadius: '12px', padding: '16px' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xl sm:text-2xl">💻</span>
                  <h2 className="text-sm sm:text-lg font-bold" style={{ color: '#2C3E50' }}>IT Speerpunten</h2>
                </div>
                <button onClick={() => openVisionModal(null, 'it')} className="text-xs" style={{ padding: '6px 10px', borderRadius: '8px', border: '2px dashed #3498DB', backgroundColor: 'transparent', color: '#3498DB', cursor: 'pointer', fontWeight: '600' }}>
                  <Plus className="w-3 h-3 inline" /> <span className="hidden sm:inline ml-1">Toevoegen</span>
                </button>
              </div>
              
              {strategicVisions.length === 0 ? (
                <div className="text-center py-6" style={{ color: '#BDC3C7' }}>
                  <p className="text-sm">Nog geen IT speerpunten</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {strategicVisions.map((vision, idx) => {
                    const colors = ['#3498DB', '#2980B9', '#1ABC9C', '#16A085'];
                    const bgColors = ['#EBF5FB', '#D4E6F1', '#E8F6F3', '#D0ECE7'];
                    const colorIdx = idx % colors.length;
                    const VisionIcon = PERSONA_ICONS.find(i => i.id === (vision.icon || 'user'))?.icon || User;
                    return (
                      <div key={vision.id} style={{ backgroundColor: bgColors[colorIdx], borderRadius: '12px', padding: '12px', position: 'relative', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{ position: 'absolute', top: '6px', right: '6px', display: 'flex', gap: '4px' }}>
                          <button onClick={() => openVisionModal(vision, 'it')} style={{ padding: '4px', borderRadius: '4px', border: 'none', backgroundColor: 'rgba(255,255,255,0.7)', color: '#7F8C8D', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                          <button onClick={() => deleteVision(vision.id, 'it')} style={{ padding: '4px', borderRadius: '4px', border: 'none', backgroundColor: 'rgba(255,255,255,0.7)', color: '#7F8C8D', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                        </div>
                        <div className="hidden sm:flex" style={{ flexDirection: 'column', alignItems: 'center', minWidth: '60px', flexShrink: 0 }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: colors[colorIdx], color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' }}>
                            <VisionIcon className="w-5 h-5" />
                          </div>
                          <h3 style={{ fontSize: '11px', fontWeight: 'bold', color: '#2C3E50', textAlign: 'center' }}>{vision.naam}</h3>
                        </div>
                        <div style={{ flex: 1, paddingTop: '4px', paddingRight: '16px' }}>
                          <h3 className="sm:hidden text-xs font-bold mb-1" style={{ color: '#2C3E50' }}>{vision.naam}</h3>
                          <p className="text-xs" style={{ color: '#2C3E50', lineHeight: '1.4', fontStyle: 'italic' }}>{vision.quote}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          </AccordionSection>

          {/* ACCORDION 4: Global Roadmap Overview - Kwartaal */}
          <AccordionSection
            id="roadmapKwartaal"
            title="2. Product Roadmap - Kwartaal View"
            icon="🗺️"
            isExpanded={expandedSections.roadmapKwartaal}
            onToggle={toggleSection}
            badge={`${getAllRoadmapItems().length} items`}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {/* Status Legend */}
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  {ROADMAP_STATUSES.map(s => (
                    <span key={s.id} className="text-xs" style={{ padding: '2px 4px', borderRadius: '10px', backgroundColor: s.bgColor, color: s.color, fontWeight: '600' }}>
                      {s.icon} <span className="hidden sm:inline">{s.label}</span>
                    </span>
                  ))}
                </div>
                <button onClick={() => openCommentModal(GENERAL_ROADMAP_ID)} className="text-xs" style={{ padding: '6px 10px', borderRadius: '8px', border: '2px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontWeight: '600' }}>
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" /> <span className="hidden sm:inline">Nieuw Item</span><span className="sm:hidden">Nieuw</span>
                </button>
              </div>
            </div>
            
            {/* Quarter Columns - Global View */}
            {(() => {
              const allItems = getAllRoadmapItems();
              const totalFteAll = allItems.reduce((sum, c) => sum + (c.fte || 0), 0);
              const plannedFte = allItems.filter(c => c.quarter && c.quarter !== 'backlog').reduce((sum, c) => sum + (c.fte || 0), 0);
              
              return (
                <>
                  <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(150px, 1fr))', gap: '8px', marginBottom: '16px', minWidth: '800px' }}>
                    {QUARTERS.map(quarter => {
                      const quarterItems = allItems.filter(c => (c.quarter || 'backlog') === quarter.id);
                      const totalFte = quarterItems.reduce((sum, c) => sum + (c.fte || 0), 0);
                      
                      return (
                        <div 
                          key={quarter.id}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                            updateCommentQuarter(data.personaId, data.commentId, quarter.id);
                          }}
                          style={{ 
                            backgroundColor: quarter.id === 'backlog' ? '#F8F9FA' : 'white',
                            borderRadius: '12px', 
                            padding: '10px',
                            border: quarter.id === 'backlog' ? '2px dashed #BDC3C7' : '2px solid #ECF0F1',
                            minHeight: '180px'
                          }}
                        >
                          <div style={{ textAlign: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #ECF0F1' }}>
                            <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#2C3E50' }}>{quarter.label}</p>
                            <p style={{ fontSize: '11px', color: '#BDC3C7' }}>{quarter.months}</p>
                            <div style={{ marginTop: '6px', padding: '4px 8px', borderRadius: '12px', backgroundColor: totalFte > 3 ? '#FDEDEC' : totalFte > 2 ? '#FEF9E7' : '#E8F8F0', display: 'inline-block' }}>
                              <span style={{ fontSize: '12px', fontWeight: 'bold', color: totalFte > 3 ? '#E74C3C' : totalFte > 2 ? '#F39C12' : '#27AE60' }}>
                                👥 {totalFte.toFixed(1)} FTE
                              </span>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {quarterItems.map(c => {
                              const status = ROADMAP_STATUSES.find(s => s.id === (c.status || 'draft')) || ROADMAP_STATUSES[0];
                              const isGeneral = c.personaId === GENERAL_ROADMAP_ID;
                              const isDone = (c.status || 'draft') === 'done';
                              const currentChecks = getChecksForStatus(c.status || 'draft');
                              const checksCompleteCount = countChecksComplete(c);
                              const allComplete = allChecksComplete(c);
                              const isFullyDone = isDone && allComplete;
                              
                              return (
                                <div
                                  key={`${c.personaId}-${c.id}`}
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData('text/plain', JSON.stringify({ personaId: c.personaId, commentId: c.id }));
                                  }}
                                  style={{
                                    backgroundColor: isDone ? 'linear-gradient(135deg, #F5EEF8 0%, #E8F8F0 100%)' : status.bgColor,
                                    background: isFullyDone 
                                      ? 'linear-gradient(135deg, #F5EEF8 0%, #FCF3CF 25%, #E8F8F0 50%, #EBF5FB 75%, #F5EEF8 100%)' 
                                      : isDone 
                                        ? 'linear-gradient(135deg, #F5EEF8 0%, #FDEBD0 50%, #E8F8F0 100%)' 
                                        : status.bgColor,
                                    borderLeft: `4px solid ${status.color}`,
                                    borderRadius: '6px',
                                    padding: '8px',
                                    cursor: 'grab',
                                    position: 'relative',
                                    boxShadow: isFullyDone 
                                      ? '0 4px 12px rgba(155, 89, 182, 0.4), 0 0 20px rgba(241, 196, 15, 0.3)' 
                                      : isDone 
                                        ? '0 2px 8px rgba(155, 89, 182, 0.3)' 
                                        : 'none'
                                  }}
                                  title={`${c.text}\n\nPersona: ${c.personaNaam}\n${c.jiraLink ? '🔗 ' + c.jiraLink : 'Geen JIRA link'}\n👥 ${c.fte || 0} FTE\n📋 ${status.label}${currentChecks.length > 0 ? `\n\n✅ Checks: ${checksCompleteCount}/${currentChecks.length}\n${currentChecks.map(ch => `${c[ch.id] ? '✓' : '○'} ${ch.label}`).join('\n')}` : ''}`}
                                  onClick={() => c.jiraLink && window.open(c.jiraLink, '_blank')}
                                >
                                  {/* Fully Done celebration effect */}
                                  {isFullyDone && (
                                    <>
                                      <div style={{ position: 'absolute', top: '-8px', right: '-8px', fontSize: '20px' }}>🏆</div>
                                      <div style={{ position: 'absolute', top: '-6px', left: '-6px', fontSize: '16px' }}>⭐</div>
                                    </>
                                  )}
                                  {isDone && !isFullyDone && (
                                    <div style={{ position: 'absolute', top: '-6px', right: '-6px', fontSize: '16px' }}>
                                      ✨
                                    </div>
                                  )}
                                  
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '8px', backgroundColor: isGeneral ? '#9B59B6' : '#FF8C42', color: 'white', fontWeight: '600' }}>
                                      {isGeneral ? '🔧 Algemeen' : c.personaNaam}
                                    </span>
                                    <span style={{ fontSize: '10px' }}>{isDone ? '🎉' : status.icon}</span>
                                  </div>
                                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#2C3E50', marginBottom: '4px', lineHeight: '1.3', textDecoration: isDone ? 'none' : 'none' }}>
                                    {isDone && '✓ '}{c.text?.length > 50 ? c.text.substring(0, 50) + '...' : c.text}
                                  </p>
                                  
                                  {/* Status Checks Indicators */}
                                  {currentChecks.length > 0 && (
                                    <div style={{ display: 'flex', gap: '2px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                      {currentChecks.map(check => (
                                        <button
                                          key={check.id}
                                          onClick={(e) => { e.stopPropagation(); toggleCommentCheck(c.personaId, c.id, check.id); }}
                                          title={`${check.label}: ${c[check.id] ? 'Ja' : 'Nee'}`}
                                          style={{
                                            fontSize: '7px',
                                            padding: '2px 3px',
                                            borderRadius: '3px',
                                            border: 'none',
                                            backgroundColor: c[check.id] ? (isDone ? '#9B59B6' : '#27AE60') : '#ECF0F1',
                                            color: c[check.id] ? 'white' : '#BDC3C7',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          {c[check.id] ? '✓' : '○'} {check.short}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Fully Done celebration badge */}
                                  {isFullyDone && (
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      gap: '4px',
                                      padding: '4px 8px', 
                                      background: 'linear-gradient(135deg, #9B59B6 0%, #E67E22 50%, #F39C12 100%)', 
                                      borderRadius: '12px',
                                      marginBottom: '4px'
                                    }}>
                                      <span style={{ fontSize: '10px' }}>🎊</span>
                                      <span style={{ fontSize: '8px', color: 'white', fontWeight: 'bold' }}>VOLTOOID!</span>
                                      <span style={{ fontSize: '10px' }}>🎊</span>
                                    </div>
                                  )}
                                  
                                  {/* Regular Done badge (not all checks complete yet) */}
                                  {isDone && !isFullyDone && (
                                    <div style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      gap: '4px',
                                      padding: '4px 8px', 
                                      backgroundColor: '#9B59B6', 
                                      borderRadius: '12px',
                                      marginBottom: '4px'
                                    }}>
                                      <span style={{ fontSize: '10px' }}>🎉</span>
                                      <span style={{ fontSize: '8px', color: 'white', fontWeight: 'bold' }}>DONE</span>
                                      <span style={{ fontSize: '10px' }}>🎉</span>
                                    </div>
                                  )}
                                  
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '10px', color: '#7F8C8D', fontWeight: '500' }}>
                                      {c.fte ? `${c.fte} FTE` : ''}
                                    </span>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                      {c.jiraLink && (
                                        <a 
                                          href={c.jiraLink} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ 
                                            fontSize: '10px', 
                                            padding: '2px 6px', 
                                            backgroundColor: '#3498DB', 
                                            color: 'white', 
                                            borderRadius: '4px', 
                                            textDecoration: 'none',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px'
                                          }}
                                          title={`Open JIRA: ${c.jiraLink}`}
                                        >
                                          🔗 JIRA
                                        </a>
                                      )}
                                      {!isDone && allComplete && <span style={{ fontSize: '10px' }} title="Klaar voor volgende status!">🎯</span>}
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); openCommentModal(c.personaId, c); }} 
                                        style={{ color: '#3498DB', background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '10px' }}
                                        title="Bewerken"
                                      >✏️</button>
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); deleteComment(c.personaId, c.id); }} 
                                        style={{ color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '10px' }}
                                        title="Verwijderen"
                                      >×</button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {quarterItems.length === 0 && (
                              <p style={{ fontSize: '11px', color: '#BDC3C7', textAlign: 'center', padding: '20px 0' }}>
                                Sleep items hierheen
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                  
                  {/* FTE Summary Bar */}
                  <div className="flex flex-wrap justify-between gap-2 sm:gap-4 p-3 sm:p-4 text-white text-xs sm:text-sm" style={{ backgroundColor: '#2C3E50', borderRadius: '8px' }}>
                    <div>
                      <span className="opacity-70">Tot: </span>
                      <span className="font-bold">{allItems.length}</span>
                    </div>
                    <div>
                      <span className="opacity-70">💡 </span>
                      <span className="font-bold" style={{ color: '#F1C40F' }}>{allItems.filter(i => (i.status || 'draft') === 'draft').length}</span>
                    </div>
                    <div>
                      <span className="opacity-70">📋 </span>
                      <span className="font-bold" style={{ color: '#3498DB' }}>{allItems.filter(i => i.status === 'ready').length}</span>
                    </div>
                    <div>
                      <span className="opacity-70">🚀 </span>
                      <span className="font-bold" style={{ color: '#27AE60' }}>{allItems.filter(i => i.status === 'development').length}</span>
                    </div>
                    <div>
                      <span className="opacity-70">🎉 </span>
                      <span className="font-bold" style={{ color: '#9B59B6' }}>{allItems.filter(i => i.status === 'done').length}</span>
                    </div>
                    <div className="hidden sm:block" style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '12px' }}>
                      <span className="opacity-70">FTE: </span>
                      <span className="font-bold" style={{ color: '#27AE60' }}>{plannedFte.toFixed(1)}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </AccordionSection>

{/* ACCORDION 3: Epics Overview */}
          <AccordionSection
            id="epics"
            title="3. Epics & Klantwensen"
            icon="🚀"
            isExpanded={expandedSections.epics}
            onToggle={toggleSection}
            badge={`${EPIC_CONFIG.length} epics`}
          >
            <div className="mb-4">
              <p className="text-sm mb-4" style={{ color: '#7F8C8D' }}>
                Klantwensen uitgewerkt als Epics met gedetailleerde flowcharts en specificaties.
              </p>
              
              {/* Epic Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {EPIC_CONFIG.map(epic => {
                  // Get status from JSONBin ideas if available
                  const ideaData = null; // Will be loaded from JSONBin
                  
                  return (
                    <div 
                      key={epic.id}
                      className="bg-white p-5 cursor-pointer transition-all hover:shadow-lg"
                      style={{ 
                        borderRadius: '12px', 
                        border: `2px solid ${epic.color}40`,
                        borderLeft: `4px solid ${epic.color}`
                      }}
                      onClick={() => navigateTo(epic.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 flex items-center justify-center text-2xl"
                            style={{ 
                              backgroundColor: epic.color + '20', 
                              borderRadius: '10px' 
                            }}
                          >
                            {epic.icon}
                          </div>
                          <div>
                            <h3 className="font-bold" style={{ color: '#2C3E50' }}>{epic.title}</h3>
                            <p className="text-xs" style={{ color: '#7F8C8D' }}>Epic • Klantwens</p>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm mb-4" style={{ color: '#7F8C8D' }}>{epic.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: epic.color + '20', color: epic.color, fontWeight: '600' }}
                        >
                          Bekijk Details →
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(window.location.origin + window.location.pathname + '#' + epic.id);
                            alert('🔗 Link gekopieerd!');
                          }}
                          className="text-xs px-2 py-1"
                          style={{ color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer' }}
                          title="Kopieer deep link"
                        >
                          🔗
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Add New Epic Card */}
                <div 
                  className="p-5 flex flex-col items-center justify-center text-center"
                  style={{ 
                    borderRadius: '12px', 
                    border: '2px dashed #BDC3C7',
                    minHeight: '180px',
                    backgroundColor: '#F8F9FA'
                  }}
                >
                  <span className="text-3xl mb-2">➕</span>
                  <p className="text-sm font-semibold" style={{ color: '#7F8C8D' }}>Nieuwe Epic</p>
                  <p className="text-xs" style={{ color: '#BDC3C7' }}>Komt binnenkort</p>
                </div>
              </div>
            </div>
          </AccordionSection>

          {/* ACCORDION 2: Journey with Bundles */}
          <AccordionSection
            id="journeys"
            title="4. Customer Journeys & Procesflows"
            icon="📋"
            isExpanded={expandedSections.journeys}
            onToggle={toggleSection}
            badge={`${journeys.length} journeys`}
          >
            {/* Journey Tabs */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                {journeys.map((journey, idx) => (
                  <div key={journey.id} className="flex items-center">
                    <button
                      onClick={() => setActiveJourneyId(journey.id)}
                      className="text-xs sm:text-sm"
                      style={{
                        padding: '6px 12px',
                        borderRadius: activeJourneyId === journey.id ? '8px 8px 0 0' : '8px',
                        border: activeJourneyId === journey.id ? '2px solid #FF8C42' : '2px solid #ECF0F1',
                        borderBottom: activeJourneyId === journey.id ? '2px solid white' : '2px solid #ECF0F1',
                        backgroundColor: activeJourneyId === journey.id ? 'white' : '#F8F9FA',
                        color: activeJourneyId === journey.id ? '#FF8C42' : '#7F8C8D',
                        cursor: 'pointer',
                        fontWeight: activeJourneyId === journey.id ? 'bold' : 'normal',
                        marginBottom: activeJourneyId === journey.id ? '-2px' : '0',
                        position: 'relative',
                        zIndex: activeJourneyId === journey.id ? 1 : 0
                      }}
                    >
                      {journey.naam}
                    </button>
                    {activeJourneyId === journey.id && (
                      <div className="flex ml-1" style={{ gap: '2px' }}>
                        <button onClick={() => openJourneyModal(journey)} title="Bewerken" style={{ padding: '4px', color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                        <button onClick={() => deleteJourney(journey.id)} title="Verwijderen" style={{ padding: '4px', color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={() => openJourneyModal()} className="text-xs" style={{ padding: '6px 10px', borderRadius: '8px', border: '2px dashed #27AE60', backgroundColor: 'transparent', color: '#27AE60', cursor: 'pointer', fontWeight: '600' }}>
                  <Plus className="w-3 h-3 inline mr-1" /> <span className="hidden sm:inline">Procesgang</span>
                </button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openBundleModal()} className="text-xs" style={{ padding: '4px 8px', borderRadius: '6px', border: '2px dashed #BDC3C7', backgroundColor: 'transparent', color: '#7F8C8D', cursor: 'pointer' }}>
                  <Square className="w-3 h-3 inline" /> <span className="hidden sm:inline ml-1">Bundel</span>
                </button>
                <button onClick={() => openStageModal()} className="text-xs" style={{ padding: '4px 8px', borderRadius: '6px', border: '2px dashed #BDC3C7', backgroundColor: 'transparent', color: '#7F8C8D', cursor: 'pointer' }}>
                  <Plus className="w-3 h-3 inline" /> <span className="hidden sm:inline ml-1">Stap</span>
                </button>
              </div>
            </div>
            
            {/* Active Journey Content */}
            {(() => {
              const activeJourney = getActiveJourney();
              const activeStages = activeJourney.stages || [];
              const activeBundles = activeJourney.bundles || [];
              
              const getStageIndices = (stageIds) => {
                return stageIds.map(id => activeStages.findIndex(s => s.id === id)).filter(i => i !== -1);
              };
              
              return (
                <>
                  {activeStages.length === 0 ? (
                    <div className="text-center py-8 sm:py-12" style={{ color: '#BDC3C7' }}>
                      <p>Nog geen stappen in deze procesgang</p>
                      <p className="text-sm mt-1">Klik op "+ Stap" om te beginnen</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 pb-4">
                      <div style={{ position: 'relative', minWidth: 'max-content', paddingTop: activeBundles.length > 0 ? '32px' : '8px', paddingBottom: '16px' }}>
                        {/* Bundle labels - now inside scrollable container */}
                        {activeBundles.map(bundle => {
                          const indices = getStageIndices(bundle.stageIds);
                          if (indices.length < 2) return null;
                          const minIdx = Math.min(...indices);
                          const maxIdx = Math.max(...indices);
                          const cardWidth = 150;
                          const gapWidth = 16;
                          const arrowWidth = 16;
                          const totalItemWidth = cardWidth + gapWidth + arrowWidth + gapWidth;
                          const left = minIdx * totalItemWidth;
                          const width = (maxIdx - minIdx) * totalItemWidth + cardWidth;
                          return (
                            <div key={`label-${bundle.id}`} style={{ position: 'absolute', top: '0', left: `${left}px`, width: `${width}px`, height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', zIndex: 5 }}>
                              <span style={{ fontSize: '11px', color: '#FF8C42', fontWeight: '600' }}>{bundle.naam}</span>
                              <div className="flex space-x-1">
                                <button onClick={() => openBundleModal(bundle)} style={{ color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><Edit3 className="w-3 h-3" /></button>
                                <button onClick={() => deleteBundle(bundle.id)} style={{ color: '#BDC3C7', background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}><Trash2 className="w-3 h-3" /></button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Bundle backgrounds - now inside scrollable container */}
                        {activeBundles.map(bundle => {
                          const indices = getStageIndices(bundle.stageIds);
                          if (indices.length < 2) return null;
                          const minIdx = Math.min(...indices);
                          const maxIdx = Math.max(...indices);
                          const cardWidth = 150;
                          const gapWidth = 16;
                          const arrowWidth = 16;
                          const totalItemWidth = cardWidth + gapWidth + arrowWidth + gapWidth;
                          const left = minIdx * totalItemWidth - 8;
                          const width = (maxIdx - minIdx) * totalItemWidth + cardWidth + 16;
                          return (
                            <div key={`bg-${bundle.id}`} style={{
                              position: 'absolute', top: '24px', left: `${left}px`, width: `${width}px`, height: 'calc(100% - 24px)',
                              backgroundColor: 'rgba(255, 140, 66, 0.08)', border: '2px dashed rgba(255, 140, 66, 0.3)',
                              borderRadius: '12px', zIndex: 0
                            }} />
                          );
                        })}

                        <div className="flex items-start" style={{ position: 'relative', zIndex: 1, gap: '16px' }}>
                        {activeStages.map((stage, idx) => {
                          const isDecision = stage.type === 'decision';
                          const isEnd = stage.type === 'end';
                          const stageColor = isDecision ? '#9B59B6' : isEnd ? '#27AE60' : '#FF8C42';
                          const stageBg = isDecision ? '#F5EEF8' : isEnd ? '#E8F8F0' : '#FFE5D9';
                          
                          // Find target stage names for decisions
                          const noBranchTarget = stage.noBranch === '__END__' ? 'EINDE' : 
                            activeStages.find(s => s.id === stage.noBranch)?.stage || '';
                          
                          return (
                          <React.Fragment key={stage.id}>
                            <div className="flex-shrink-0 relative group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              {/* Main stage container */}
                              <div style={{ 
                                width: isEnd ? '100px' : '150px', 
                                minHeight: isEnd ? '70px' : isDecision ? '100px' : '130px',
                                backgroundColor: stageBg, 
                                borderRadius: isEnd ? '50px' : isDecision ? '8px' : '10px',
                                padding: '10px', 
                                display: 'flex', 
                                flexDirection: 'column',
                                transform: isDecision ? 'rotate(0deg)' : 'none',
                                border: isDecision ? `3px solid ${stageColor}` : 'none',
                                position: 'relative'
                              }}>
                                {/* Diamond shape for decision */}
                                {isDecision && (
                                  <div style={{
                                    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%) rotate(45deg)',
                                    width: '24px', height: '24px', backgroundColor: stageColor, borderRadius: '4px'
                                  }} />
                                )}
                                
                                {/* Stage number */}
                                {!isEnd && (
                                  <div style={{ 
                                    position: 'absolute', top: isDecision ? '4px' : '8px', left: '8px', 
                                    width: '26px', height: '26px', borderRadius: '50%', 
                                    backgroundColor: stageColor, color: 'white', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    fontSize: '13px', fontWeight: 'bold', zIndex: 10 
                                  }}>{idx + 1}</div>
                                )}
                        
                                {/* Actions */}
                                <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '2px', opacity: 0.3 }} className="group-hover:opacity-100"
                                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'} onMouseLeave={(e) => e.currentTarget.style.opacity = '0.3'}>
                                  {idx > 0 && <button onClick={() => moveStage(stage.id, 'left')} style={{ padding: '2px 4px', background: 'none', border: 'none', color: '#7F8C8D', cursor: 'pointer', fontSize: '9px' }}>◀</button>}
                                  {idx < activeStages.length - 1 && <button onClick={() => moveStage(stage.id, 'right')} style={{ padding: '2px 4px', background: 'none', border: 'none', color: '#7F8C8D', cursor: 'pointer', fontSize: '9px' }}>▶</button>}
                                  <button onClick={() => openStageModal(stage)} style={{ padding: '2px', background: 'none', border: 'none', color: '#7F8C8D', cursor: 'pointer' }}><Edit3 className="w-3 h-3" /></button>
                                  <button onClick={() => deleteStage(stage.id)} style={{ padding: '2px', background: 'none', border: 'none', color: '#7F8C8D', cursor: 'pointer' }}><Trash2 className="w-3 h-3" /></button>
                                </div>
                        
                                {/* Content */}
                                <div style={{ textAlign: 'center', marginBottom: '8px', flex: '0 0 auto', marginTop: isEnd ? '8px' : '28px' }}>
                                  {isEnd && <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>⬤</span>}
                                  {isDecision && <span style={{ fontSize: '12px', color: stageColor, fontWeight: '600' }}>BESLISSING</span>}
                                  <p className="font-bold" style={{ color: '#2C3E50', fontSize: '13px', marginBottom: '4px' }}>{stage.stage}</p>
                                  {stage.beschrijving && <p style={{ color: '#7F8C8D', fontSize: '11px', lineHeight: '1.3' }}>{stage.beschrijving}</p>}
                                </div>
                        
                                {/* Primary Persona */}
                                {stage.primaryPersona && personas[stage.primaryPersona] && !isEnd && (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '8px', padding: '6px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: stageColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {(() => {
                                        const persona = personas[stage.primaryPersona];
                                        const PersonaIcon = persona?.icon ? PERSONA_ICONS.find(i => i.id === persona.icon)?.icon || User : User;
                                        return <PersonaIcon style={{ width: '12px', height: '12px' }} />;
                                      })()}
                                    </div>
                                    <span style={{ fontSize: '10px', fontWeight: '600', color: '#2C3E50' }}>{personas[stage.primaryPersona].naam}</span>
                                  </div>
                                )}
                        
                                {/* Links for steps */}
                                {stage.type === 'step' && stage.links && stage.links.length > 0 && (
                                  <div style={{ marginTop: 'auto', borderTop: `1px solid ${stageColor}33`, paddingTop: '8px', flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    {stage.links.map((link, li) => (
                                      <a key={li} href={link.url} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'block', fontSize: '10px', color: stageColor, textDecoration: 'none', padding: '3px 6px', borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {link.naam} →
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Decision branches visualization */}
                              {isDecision && (
                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                  <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                                    <span style={{ color: '#27AE60', fontWeight: '600' }}>✓ Ja →</span>
                                    <span style={{ color: '#E67E22', fontWeight: '600' }}>✗ {stage.noBranchLabel || 'Nee'} ↓</span>
                                  </div>
                                  {noBranchTarget && (
                                    <div style={{ 
                                      marginTop: '4px', padding: '6px 12px', 
                                      backgroundColor: stage.noBranch === '__END__' ? '#E8F8F0' : '#FEF3E2',
                                      borderRadius: stage.noBranch === '__END__' ? '20px' : '6px',
                                      border: `2px dashed ${stage.noBranch === '__END__' ? '#27AE60' : '#E67E22'}`,
                                      fontSize: '10px', fontWeight: '600',
                                      color: stage.noBranch === '__END__' ? '#27AE60' : '#E67E22'
                                    }}>
                                      {stage.noBranch === '__END__' ? '⬤ ' : '→ '}{noBranchTarget}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Arrow to next - show "Ja" for decisions */}
                            {idx < activeStages.length - 1 && !isEnd && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '65px' }}>
                                {isDecision && <span style={{ fontSize: '10px', color: '#27AE60', fontWeight: '600', marginBottom: '2px' }}>Ja</span>}
                                <ArrowRight className="flex-shrink-0" style={{ color: isDecision ? '#27AE60' : '#FF8C42', width: '16px', height: '16px' }} />
                              </div>
                            )}
                          </React.Fragment>
                        );})}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </AccordionSection>

          {/* ACCORDION 3: Personas Grid */}
          <AccordionSection
            id="personas"
            title="5. Persona's & Stakeholders"
            icon="👥"
            isExpanded={expandedSections.personas}
            onToggle={toggleSection}
            badge={`${Object.keys(personas).length} persona's`}
          >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-6" style={{ borderRadius: '12px' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2"><Building2 className="w-5 h-5" style={{ color: '#FF8C42' }} /><h3 className="text-lg font-bold" style={{ color: '#2C3E50' }}>Zorginstelling</h3></div>
                <span className="px-2 py-1 text-xs font-bold" style={{ backgroundColor: '#FFE5D9', color: '#FF8C42', borderRadius: '4px' }}>KLANT</span>
              </div>
              <div className="space-y-3">
                {Object.entries(personas).filter(([k, p]) => p?.organisatie === 'Zorginstelling').map(([k, p]) => <PersonaCard key={k} persona={p} />)}
                <button onClick={() => openNewPersonaModal('Zorginstelling')} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px dashed #BDC3C7', backgroundColor: 'transparent', color: '#7F8C8D', cursor: 'pointer', fontSize: '13px' }}><Plus className="w-4 h-4 inline mr-1" /> Nieuwe Persona</button>
              </div>
            </div>

            <div className="bg-white p-6" style={{ borderRadius: '12px' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2"><Stethoscope className="w-5 h-5" style={{ color: '#FF8C42' }} /><h3 className="text-lg font-bold" style={{ color: '#2C3E50' }}>GeriCall</h3></div>
                <span className="px-2 py-1 text-xs font-bold" style={{ backgroundColor: '#ECF0F1', color: '#7F8C8D', borderRadius: '4px' }}>LEVERANCIER</span>
              </div>
              <div className="space-y-3">
                {Object.entries(personas).filter(([k, p]) => p?.organisatie === 'GeriCall').map(([k, p]) => <PersonaCard key={k} persona={p} />)}
                <button onClick={() => openNewPersonaModal('GeriCall')} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px dashed #BDC3C7', backgroundColor: 'transparent', color: '#7F8C8D', cursor: 'pointer', fontSize: '13px' }}><Plus className="w-4 h-4 inline mr-1" /> Nieuwe Persona</button>
              </div>
            </div>

            <div className="bg-white" style={{ borderRadius: '12px', minHeight: '300px' }}><DetailPanel persona={selectedPersona} /></div>
          </div>
          </AccordionSection>

          {/* ACCORDION 5: Gantt Chart - Maand View */}
          <AccordionSection
            id="gantt"
            title="6. Product Roadmap - Maand View"
            icon="📊"
            isExpanded={expandedSections.gantt}
            onToggle={toggleSection}
            badge={`${ganttItems.length} initiatieven`}
          >
            <GanttChart />
          </AccordionSection>

          {/* ACCORDION 6: IT Costs - Only visible for admin */}
          {userRole === 'admin' && (
            <AccordionSection
              id="costs"
              title="7. IT Kosten Overzicht"
              icon="💰"
              isExpanded={expandedSections.costs}
              onToggle={toggleSection}
              badge={itCosts.length > 0 ? `€${Math.round(itCosts.reduce((sum, c) => sum + (c.amount * (COST_FREQUENCIES.find(f => f.id === c.frequency)?.multiplier || 1)), 0)).toLocaleString('nl-NL')}/jaar` : 'Geen kosten'}
            >
              <ITCostsSection />
            </AccordionSection>
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // ARTS DASHBOARD VIEW
  // ============================================
  const ArtsDashboardView = () => {
    const diensten = { volgende: { datum: "Za 11 jan", tijd: "18:00-06:00", locatie: "Den Haag" }, afgelopenMaand: { consults: 23, uren: 42.5 } };
    const nabijConsults = [
      { id: 1, type: "Telefonisch", urgentie: "Gemiddeld", afstand: "2.3 km", zorginstelling: "Woonzorgcentrum Duinzicht", tijd: "12 min", vraag: "Onrustige bewoner" },
      { id: 2, type: "Visite", urgentie: "Laag", afstand: "5.8 km", zorginstelling: "Residentie Parkrust", tijd: "23 min", vraag: "Evaluatie medicatie" }
    ];
    const komendeDiensten = [
      { datum: "Za 11 jan", tijd: "18:00-06:00", locatie: "Den Haag", status: "Bevestigd" },
      { datum: "Di 14 jan", tijd: "18:00-06:00", locatie: "Rotterdam", status: "Bevestigd" },
      { datum: "Vr 17 jan", tijd: "18:00-06:00", locatie: "Den Haag", status: "Open" }
    ];

    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
        <header className="bg-white" style={{ borderBottom: '3px solid #FF8C42' }}>
          <div className="max-w-7xl mx-auto px-8 py-6">
            <BackButton />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '12px', padding: '8px' }}>
                  <img src={logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold" style={{ color: '#2C3E50' }}>GeriCall Arts Dashboard</h1>
                  <p className="text-sm" style={{ color: '#7F8C8D' }}>Samen sterker in de zorg</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Bell className="w-6 h-6" style={{ color: '#7F8C8D' }} />
                <div className="text-right">
                  <p className="font-semibold" style={{ color: '#2C3E50' }}>Dr. Sarah van Berg</p>
                  <p className="text-sm" style={{ color: '#7F8C8D' }}>Specialist Ouderengeneeskunde</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-5" style={{ borderLeft: '4px solid #FF8C42', borderRadius: '12px' }}>
              <p className="text-sm" style={{ color: '#7F8C8D' }}>Volgende dienst</p>
              <p className="text-xl font-bold" style={{ color: '#2C3E50' }}>{diensten.volgende.datum}</p>
              <p className="text-sm" style={{ color: '#BDC3C7' }}>{diensten.volgende.tijd}</p>
            </div>
            <div className="bg-white p-5" style={{ borderRadius: '12px' }}>
              <p className="text-sm" style={{ color: '#7F8C8D' }}>Deze maand</p>
              <p className="text-xl font-bold" style={{ color: '#2C3E50' }}>{diensten.afgelopenMaand.consults} patiënten</p>
              <p className="text-sm" style={{ color: '#BDC3C7' }}>{diensten.afgelopenMaand.uren}u</p>
            </div>
            <div className="bg-white p-5" style={{ borderRadius: '12px' }}>
              <p className="text-sm" style={{ color: '#7F8C8D' }}>Kudos</p>
              <p className="text-xl font-bold" style={{ color: '#2C3E50' }}>127</p>
              <p className="text-sm" style={{ color: '#FF8C42' }}>+15 deze week</p>
            </div>
            <div className="p-5 text-white" style={{ backgroundColor: '#FF8C42', borderRadius: '12px' }}>
              <p className="text-sm opacity-90">Hulp nodig</p>
              <p className="text-xl font-bold">{nabijConsults.length} consults</p>
              <p className="text-sm opacity-90">in de buurt</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-6">
              <div className="bg-white" style={{ borderRadius: '12px' }}>
                <div className="p-5" style={{ borderBottom: '2px solid #FF8C42' }}>
                  <h2 className="text-lg font-bold" style={{ color: '#2C3E50' }}>Nabije Consults</h2>
                </div>
                <div className="p-5 space-y-3">
                  {nabijConsults.map(c => (
                    <div key={c.id} className="p-4" style={{ backgroundColor: '#FFE5D9', borderRadius: '8px' }}>
                      <div className="flex justify-between mb-2">
                        <span className="font-semibold text-sm" style={{ color: '#FF8C42' }}>{c.type} • {c.urgentie}</span>
                        <span className="text-xs" style={{ color: '#7F8C8D' }}>{c.afstand}</span>
                      </div>
                      <p className="font-bold" style={{ color: '#2C3E50' }}>{c.zorginstelling}</p>
                      <p className="text-sm" style={{ color: '#7F8C8D' }}>{c.vraag}</p>
                      <button className="w-full mt-3 py-2 text-white font-semibold" style={{ backgroundColor: '#FF8C42', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>Contact opnemen</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white" style={{ borderRadius: '12px' }}>
                <div className="p-5" style={{ borderBottom: '2px solid #BDC3C7' }}>
                  <h2 className="text-lg font-bold" style={{ color: '#2C3E50' }}>Agenda</h2>
                </div>
                <div className="p-5 space-y-2">
                  {komendeDiensten.map((d, i) => (
                    <div key={i} className="p-3 flex justify-between items-center" style={{ backgroundColor: '#F8F9FA', borderRadius: '6px' }}>
                      <div>
                        <p className="font-semibold" style={{ color: '#2C3E50' }}>{d.datum}</p>
                        <p className="text-sm" style={{ color: '#7F8C8D' }}>{d.tijd} • {d.locatie}</p>
                      </div>
                      <span className="px-3 py-1 text-sm font-semibold" style={{ backgroundColor: d.status === 'Open' ? '#FFE5D9' : '#ECF0F1', color: d.status === 'Open' ? '#FF8C42' : '#2C3E50', borderRadius: '6px' }}>{d.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white" style={{ borderRadius: '12px' }}>
              <div className="p-5" style={{ borderBottom: '2px solid #BDC3C7' }}>
                <h2 className="text-lg font-bold" style={{ color: '#2C3E50' }}>Team Chat</h2>
              </div>
              <div className="p-4 space-y-2" style={{ height: '300px', overflowY: 'auto' }}>
                <div className="p-3 bg-white" style={{ borderLeft: '3px solid #ECF0F1', borderRadius: '6px' }}>
                  <p className="text-sm font-bold" style={{ color: '#2C3E50' }}>Dr. Jansen</p>
                  <p className="text-sm" style={{ color: '#7F8C8D' }}>Fijne dienst gehad! 😊</p>
                </div>
                <div className="p-3 bg-white" style={{ borderLeft: '3px solid #ECF0F1', borderRadius: '6px' }}>
                  <p className="text-sm font-bold" style={{ color: '#2C3E50' }}>Dr. de Vries</p>
                  <p className="text-sm" style={{ color: '#7F8C8D' }}>Top! Mooie case gehad.</p>
                </div>
              </div>
              <div className="p-4" style={{ borderTop: '1px solid #ECF0F1' }}>
                <input type="text" placeholder="Bericht..." className="w-full px-3 py-2" style={{ border: '2px solid #BDC3C7', borderRadius: '6px' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // ARTS MOBILE VIEW
  // ============================================
  const ArtsMobileView = () => {
    const [tab, setTab] = useState('home');
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#ECF0F1', padding: '40px' }}>
        <div style={{ position: 'absolute', top: '20px', left: '20px' }}><BackButton /></div>
        <div style={{ width: '375px', height: '812px', backgroundColor: 'black', borderRadius: '40px', padding: '12px' }}>
          <div style={{ height: '100%', backgroundColor: '#F8F9FA', borderRadius: '32px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="bg-white p-4" style={{ borderBottom: '2px solid #FF8C42' }}>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '8px' }}>
                  <span className="text-white font-bold">G</span>
                </div>
                <div><h1 className="font-bold" style={{ color: '#2C3E50' }}>GeriCall</h1></div>
              </div>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {tab === 'home' && (
                <div className="space-y-4">
                  <div className="bg-white p-4" style={{ borderRadius: '12px', borderLeft: '4px solid #FF8C42' }}>
                    <p className="text-sm" style={{ color: '#7F8C8D' }}>Hoi Sarah! 👋</p>
                    <p className="font-bold" style={{ color: '#2C3E50' }}>23 patiënten geholpen</p>
                    <p className="text-xs" style={{ color: '#FF8C42' }}>127 kudos</p>
                  </div>
                  <div className="bg-white p-4" style={{ borderRadius: '12px' }}>
                    <h3 className="font-bold mb-3" style={{ color: '#2C3E50' }}>Nabije Consults</h3>
                    <div className="p-3" style={{ backgroundColor: '#FFE5D9', borderRadius: '8px' }}>
                      <p className="text-xs font-semibold" style={{ color: '#FF8C42' }}>Telefonisch • Gemiddeld</p>
                      <p className="font-bold text-sm" style={{ color: '#2C3E50' }}>Duinzicht</p>
                      <p className="text-xs" style={{ color: '#7F8C8D' }}>2.3 km</p>
                    </div>
                  </div>
                </div>
              )}
              {tab === 'diensten' && (
                <div className="space-y-4">
                  <h2 className="font-bold" style={{ color: '#2C3E50' }}>Open Diensten</h2>
                  <div className="bg-white p-4" style={{ borderRadius: '12px' }}>
                    <p className="font-bold" style={{ color: '#2C3E50' }}>Nachtdienst</p>
                    <p className="text-sm" style={{ color: '#7F8C8D' }}>Za 18:00 - Zo 06:00</p>
                    <button className="w-full mt-3 py-2 text-white font-semibold" style={{ backgroundColor: '#FF8C42', borderRadius: '6px', border: 'none' }}>Claim</button>
                  </div>
                </div>
              )}
              {tab === 'profiel' && (
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '50%' }}>
                    <span className="text-white font-bold text-2xl">SB</span>
                  </div>
                  <p className="font-bold" style={{ color: '#2C3E50' }}>Dr. Sarah van Berg</p>
                  <p className="text-sm" style={{ color: '#7F8C8D' }}>Specialist Ouderengeneeskunde</p>
                </div>
              )}
            </div>
            
            <div className="bg-white p-2" style={{ borderTop: '1px solid #ECF0F1' }}>
              <div className="grid grid-cols-3 gap-1">
                {[{id:'home',icon:Home,label:'Home'},{id:'diensten',icon:Calendar,label:'Diensten'},{id:'profiel',icon:User,label:'Profiel'}].map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} className="flex flex-col items-center py-2"
                    style={{ backgroundColor: tab === t.id ? '#FFE5D9' : 'transparent', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                    <t.icon className="w-5 h-5" style={{ color: tab === t.id ? '#FF8C42' : '#BDC3C7' }} />
                    <span className="text-xs" style={{ color: tab === t.id ? '#FF8C42' : '#7F8C8D' }}>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // INTAKE APP VIEW (NTS-based)
  // ============================================
  const IntakeAppView = () => {
    const [step, setStep] = useState('patient');
    const [patient, setPatient] = useState(null);
    const [eventType, setEventType] = useState(null);
    const [answers, setAnswers] = useState({});
    const [qIdx, setQIdx] = useState(0);

    const patients = [
      { id: 1, naam: "Mevr. de Jong", kamer: "2.14", leeftijd: 87 },
      { id: 2, naam: "Dhr. Bakker", kamer: "1.08", leeftijd: 92 }
    ];

    const events = [
      { id: 'val', label: 'Gevallen', icon: AlertCircle, color: '#E67332', flow: [
        { q: 'Heeft de bewoner pijn?', key: 'pijn', high: true },
        { q: 'Kan niet meer bewegen?', key: 'beweging', high: true },
        { q: 'Bewusteloos geweest?', key: 'bewusteloos', high: true }
      ]},
      { id: 'pijn', label: 'Pijn/ongemak', icon: Activity, color: '#FF8C42', flow: [
        { q: 'Plotseling gekomen?', key: 'plotseling', high: true },
        { q: 'Erg onrustig?', key: 'onrustig', high: true }
      ]},
      { id: 'ademhaling', label: 'Ademhaling', icon: Activity, color: '#E67332', flow: [
        { q: 'Benauwd?', key: 'benauwd', high: true },
        { q: 'Lippen/nagels blauw?', key: 'blauw', high: true }
      ]},
      { id: 'verward', label: 'Verward', icon: Brain, color: '#FF8C42', flow: [
        { q: 'Nieuw (< 24u)?', key: 'nieuw', high: true },
        { q: 'Agressief?', key: 'agressief', high: true }
      ]}
    ];

    const urgency = () => {
      if (!eventType) return 'laag';
      const hasHigh = eventType.flow.some(q => q.high && answers[q.key] === true);
      return hasHigh ? 'hoog' : 'gemiddeld';
    };

    const DirectCallBtn = () => (
      <button className="w-full p-4 flex items-center justify-center space-x-2 text-white font-bold"
        style={{ backgroundColor: '#E67332', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>
        <Phone className="w-5 h-5" /><span>Direct arts aan de lijn</span>
      </button>
    );

    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#ECF0F1', padding: '40px' }}>
        <div style={{ position: 'absolute', top: '20px', left: '20px' }}><BackButton /></div>
        <div style={{ width: '375px', height: '812px', backgroundColor: 'black', borderRadius: '40px', padding: '12px' }}>
          <div style={{ height: '100%', backgroundColor: '#F8F9FA', borderRadius: '32px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="bg-white p-4" style={{ borderBottom: '3px solid #FF8C42' }}>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#FF8C42', borderRadius: '8px' }}>
                  <span className="text-white font-bold">G</span>
                </div>
                <div>
                  <h1 className="font-bold" style={{ color: '#2C3E50' }}>Nieuw Consult</h1>
                  <p className="text-xs" style={{ color: '#7F8C8D' }}>{step === 'patient' ? 'Kies bewoner' : step === 'event' ? 'Wat is er?' : step === 'flow' ? 'Vragen' : 'Klaar'}</p>
                </div>
              </div>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              {step === 'patient' && (
                <div className="space-y-3">
                  <DirectCallBtn />
                  <p className="text-center text-sm" style={{ color: '#7F8C8D' }}>of kies bewoner:</p>
                  {patients.map(p => (
                    <button key={p.id} onClick={() => { setPatient(p); setStep('event'); }}
                      className="w-full p-4 flex items-center space-x-3 bg-white" style={{ borderRadius: '12px', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                      <div className="w-10 h-10 flex items-center justify-center font-bold" style={{ backgroundColor: '#ECF0F1', borderRadius: '8px', color: '#2C3E50' }}>{p.naam[0]}</div>
                      <div><p className="font-bold" style={{ color: '#2C3E50' }}>{p.naam}</p><p className="text-sm" style={{ color: '#7F8C8D' }}>Kamer {p.kamer}</p></div>
                    </button>
                  ))}
                </div>
              )}

              {step === 'event' && (
                <div className="space-y-3">
                  <DirectCallBtn />
                  <p className="text-center text-sm" style={{ color: '#7F8C8D' }}>of kies klacht:</p>
                  <div className="grid grid-cols-2 gap-3">
                    {events.map(e => (
                      <button key={e.id} onClick={() => { setEventType(e); setStep('flow'); setQIdx(0); setAnswers({}); }}
                        className="p-4 flex flex-col items-center" style={{ backgroundColor: 'white', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>
                        <e.icon className="w-8 h-8 mb-2" style={{ color: e.color }} />
                        <span className="text-sm font-bold" style={{ color: '#2C3E50' }}>{e.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 'flow' && eventType && (
                <div className="space-y-4">
                  <DirectCallBtn />
                  {patient && (
                    <div className="bg-white p-3" style={{ borderRadius: '8px', borderLeft: '3px solid #FF8C42' }}>
                      <p className="text-sm" style={{ color: '#7F8C8D' }}>Bewoner: <strong style={{ color: '#2C3E50' }}>{patient.naam}</strong></p>
                    </div>
                  )}
                  {qIdx < eventType.flow.length ? (
                    <div className="bg-white p-5" style={{ borderRadius: '12px' }}>
                      <p className="text-sm mb-2" style={{ color: '#7F8C8D' }}>Vraag {qIdx + 1}/{eventType.flow.length}</p>
                      <h3 className="text-lg font-bold mb-4" style={{ color: '#2C3E50' }}>{eventType.flow[qIdx].q}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => { setAnswers({...answers, [eventType.flow[qIdx].key]: true}); setQIdx(qIdx + 1); }}
                          className="p-6 font-bold text-white" style={{ backgroundColor: '#FF8C42', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>JA</button>
                        <button onClick={() => { setAnswers({...answers, [eventType.flow[qIdx].key]: false}); setQIdx(qIdx + 1); }}
                          className="p-6 font-bold" style={{ backgroundColor: '#ECF0F1', color: '#2C3E50', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>NEE</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: urgency() === 'hoog' ? '#E67332' : '#FF8C42', borderRadius: '50%' }}>
                        <Check className="w-10 h-10 text-white" />
                      </div>
                      <h2 className="text-xl font-bold mb-2" style={{ color: '#2C3E50' }}>Consult aangemaakt!</h2>
                      <p className="mb-4" style={{ color: '#7F8C8D' }}>
                        {urgency() === 'hoog' ? '🚨 SPOED - Arts belt binnen 5 min' : '⏱️ Arts belt vandaag terug'}
                      </p>
                      <button onClick={() => { setStep('patient'); setPatient(null); setEventType(null); setAnswers({}); setQIdx(0); }}
                        className="w-full py-3 font-semibold" style={{ backgroundColor: '#ECF0F1', borderRadius: '8px', border: 'none', cursor: 'pointer', color: '#2C3E50' }}>Nieuw consult</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div>
      {isLoading && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RefreshCw className="w-12 h-12 animate-spin" style={{ color: '#FF8C42' }} />
        </div>
      )}
      
      {/* Comment Modal */}
      {showCommentModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && closeCommentModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '550px', padding: '16px', border: `3px solid ${editingComment ? '#3498DB' : '#FF8C42'}`, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base sm:text-lg font-bold mb-1" style={{ color: '#2C3E50' }}>
              {editingComment ? '✏️ Roadmap Item Bewerken' : '🗺️ Nieuw Roadmap Item'}
            </h3>
            <p style={{ fontSize: '12px', color: '#7F8C8D', marginBottom: '16px' }}>
              {commentPersonaId === GENERAL_ROADMAP_ID ? (
                <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#9B59B6', color: 'white' }}>🔧 Algemeen / Infrastructuur</span>
              ) : commentPersonaId ? (
                <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#FF8C42', color: 'white' }}>
                  👤 {Object.values(personas).find(p => p.id === commentPersonaId)?.naam || 'Persona'}
                </span>
              ) : (
                <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#3498DB', color: 'white' }}>📋 Selecteer categorie hieronder</span>
              )}
            </p>
            <form onSubmit={(e) => { e.preventDefault(); addComment(); }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Aanvrager:</label>
                  <input ref={commentNameRef} type="text" defaultValue={editingComment?.name || userName} key={editingComment?.id || 'new'} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Status:</label>
                  <select ref={commentStatusRef} defaultValue={editingComment?.status || 'draft'} key={`status-${editingComment?.id || 'new'}`} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white', fontSize: '14px' }}>
                    {ROADMAP_STATUSES.map(s => (
                      <option key={s.id} value={s.id}>{s.icon} {s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Feature titel:</label>
                <textarea ref={commentTextRef} rows={2} defaultValue={editingComment?.text || ''} key={`text-${editingComment?.id || 'new'}`} placeholder="Beschrijf de gewenste feature..." style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>🔗 JIRA Link:</label>
                <input ref={commentJiraRef} type="url" defaultValue={editingComment?.jiraLink || ''} key={`jira-${editingComment?.id || 'new'}`} placeholder="https://jira.company.com/browse/PROJ-123" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>📅 Quarter:</label>
                  <select ref={commentQuarterRef} defaultValue={editingComment?.quarter || 'backlog'} key={`quarter-${editingComment?.id || 'new'}`} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white', fontSize: '14px' }}>
                    {QUARTERS.map(q => (
                      <option key={q.id} value={q.id}>{q.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>👥 FTE/Quarter:</label>
                  <input ref={commentFteRef} type="number" step="0.1" min="0" defaultValue={editingComment?.fte || ''} key={`fte-${editingComment?.id || 'new'}`} placeholder="bijv. 1.5" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', fontSize: '14px' }} />
                </div>
              </div>
              
              {/* Status-specific Approval Checks */}
              {(() => {
                const currentStatus = editingComment?.status || 'draft';
                const checks = STATUS_CHECKS[currentStatus] || [];
                const statusConfig = ROADMAP_STATUSES.find(s => s.id === currentStatus);
                const isDone = currentStatus === 'done';
                const allChecked = checks.every(check => editingComment?.[check.id]);
                
                if (checks.length === 0) return null;
                
                return (
                  <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: statusConfig?.bgColor || '#F8F9FA', borderRadius: '8px', border: `2px solid ${statusConfig?.color || '#BDC3C7'}` }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: statusConfig?.color || '#2C3E50' }}>
                      {isDone ? '🏆' : '✅'} {statusConfig?.icon} {statusConfig?.label} Checks
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {checks.map(check => (
                        <label key={check.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '6px' }}>
                          <input 
                            type="checkbox" 
                            id={check.id}
                            defaultChecked={editingComment?.[check.id] || false}
                            key={`${check.id}-${editingComment?.id || 'new'}`}
                            style={{ width: '18px', height: '18px', accentColor: statusConfig?.color || '#F1C40F' }}
                          />
                          <span style={{ fontSize: '12px', color: '#2C3E50' }}>{check.label}</span>
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '10px', color: '#7F8C8D', marginTop: '8px' }}>
                      {isDone 
                        ? '🎉 Alle 4 checks = Volledig afgerond & gevierd!' 
                        : '💡 Alle 4 checks afronden om naar volgende status te gaan'}
                    </p>
                    
                    {/* Extra celebration for Done with all checks */}
                    {isDone && allChecked && (
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '12px', 
                        background: 'linear-gradient(135deg, #9B59B6 0%, #E74C3C 50%, #F39C12 100%)', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <span style={{ fontSize: '24px' }}>🎊</span>
                        <span style={{ fontSize: '24px' }}>🏆</span>
                        <span style={{ fontSize: '24px' }}>⭐</span>
                        <p style={{ color: 'white', fontWeight: 'bold', fontSize: '14px', margin: '8px 0 0 0' }}>
                          VOLLEDIG AFGEROND! GEWELDIG WERK!
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeCommentModal} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: editingComment ? '#3498DB' : '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>
                  {editingComment ? '💾 Opslaan' : 'Toevoegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Persona Modal */}
      {editingPersona && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={(e) => e.target === e.currentTarget && cancelPersonaEdit()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-3 sm:p-4" style={{ borderBottom: '3px solid #FF8C42', backgroundColor: '#FFF9E6' }}>
              <h2 className="text-base sm:text-xl font-bold" style={{ color: '#2C3E50' }}>✏️ Edit: {editForm.naam}</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); savePersonaEdit(); }}>
              <div className="p-4 sm:p-5" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Naam</label><input ref={editNaamRef} defaultValue={editForm.naam} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                  <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Rol</label><input ref={editRolRef} defaultValue={editForm.rol} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                </div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Beschrijving</label><textarea ref={editBeschrijvingRef} defaultValue={editForm.beschrijving} rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: '13px', fontWeight: '600', marginBottom: '4px' }}>Frequentie</label><input ref={editFrequentieRef} defaultValue={editForm.frequentie} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><IconSelector selectedIcon={editIcon} onSelectIcon={setEditIcon} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Uitdagingen (1/regel)</label><textarea ref={editUitdagingenRef} defaultValue={editForm.uitdagingen} rows={4} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Behoeften (1/regel)</label><textarea ref={editBehoeftenRef} defaultValue={editForm.behoeften} rows={4} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>KPIs (1/regel)</label><textarea ref={editKpisRef} defaultValue={editForm.kpis} rows={3} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Toekomstig Scherm</label><input ref={editToekomstigSchermRef} defaultValue={editForm.toekomstigScherm} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={cancelPersonaEdit} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>💾 Opslaan</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* New Persona Modal */}
      {showNewPersonaModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={(e) => e.target === e.currentTarget && closeNewPersonaModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '3px solid #27AE60', backgroundColor: '#E8F8F0' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#2C3E50' }}>➕ Nieuwe Persona ({newPersonaOrg})</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveNewPersona(); }}>
              <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Naam *</label><input ref={newNaamRef} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #27AE60', boxSizing: 'border-box' }} /></div>
                  <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Rol</label><input ref={newRolRef} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                </div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Beschrijving</label><textarea ref={newBeschrijvingRef} rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Frequentie</label><input ref={newFrequentieRef} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><IconSelector selectedIcon={newPersonaIcon} onSelectIcon={setNewPersonaIcon} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Uitdagingen</label><textarea ref={newUitdagingenRef} rows={3} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Behoeften</label><textarea ref={newBehoeftenRef} rows={3} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div style={{ marginBottom: '12px' }}><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>KPIs</label><textarea ref={newKpisRef} rows={2} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
                <div><label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Toekomstig Scherm</label><input ref={newToekomstigSchermRef} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} /></div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeNewPersonaModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#27AE60', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>➕ Toevoegen</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stage Modal */}
      {showStageModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={(e) => e.target === e.currentTarget && closeStageModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '3px solid #FF8C42', backgroundColor: '#FFF5EE' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50' }}>{editingStageId ? '✏️ Stap Bewerken' : '➕ Nieuwe Stap'}</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveStage(); }}>
              <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                {/* Type Selector */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Type</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { id: 'step', label: '📦 Stap', color: '#FF8C42', bg: '#FFE5D9' },
                      { id: 'decision', label: '◇ Beslissing', color: '#9B59B6', bg: '#F5EEF8' },
                      { id: 'end', label: '⬤ Einde', color: '#27AE60', bg: '#E8F8F0' }
                    ].map(t => (
                      <button key={t.id} type="button" onClick={() => setStageForm({...stageForm, type: t.id})}
                        style={{ flex: 1, padding: '12px', borderRadius: '8px', border: stageForm.type === t.id ? `2px solid ${t.color}` : '2px solid #ECF0F1',
                          backgroundColor: stageForm.type === t.id ? t.bg : 'white', color: stageForm.type === t.id ? t.color : '#7F8C8D',
                          cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                    {stageForm.type === 'decision' ? 'Vraag *' : stageForm.type === 'end' ? 'Eindstatus *' : 'Naam *'}
                  </label>
                  <input ref={stageNaamRef} defaultValue={stageForm.stage} 
                    placeholder={stageForm.type === 'decision' ? 'bijv. Is de patient stabiel?' : stageForm.type === 'end' ? 'bijv. Afgerond' : 'bijv. Intake gesprek'}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #FF8C42', boxSizing: 'border-box' }} />
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Beschrijving</label>
                  <textarea ref={stageBeschrijvingRef} defaultValue={stageForm.beschrijving} rows={2} 
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box' }} />
                </div>

                {/* Decision branches */}
                {stageForm.type === 'decision' && (
                  <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#F5EEF8', borderRadius: '8px', border: '2px solid #9B59B6' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#9B59B6', marginBottom: '12px' }}>◇ Vertakkingen</h4>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#27AE60' }}>✓ Ja → gaat naar:</label>
                      <select value={stageForm.yesBranch} onChange={(e) => setStageForm({...stageForm, yesBranch: e.target.value})}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #27AE60', backgroundColor: 'white', boxSizing: 'border-box' }}>
                        <option value="">Volgende stap in lijn →</option>
                        {(getActiveJourney().stages || []).filter(s => s.id !== editingStageId).map(s => (
                          <option key={s.id} value={s.id}>{s.type === 'end' ? '⬤' : s.type === 'decision' ? '◇' : '📦'} {s.stage}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#E67E22' }}>✗ Nee label:</label>
                      <input value={stageForm.noBranchLabel} onChange={(e) => setStageForm({...stageForm, noBranchLabel: e.target.value})}
                        placeholder="bijv. Nee, Afgewezen, Niet akkoord"
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #BDC3C7', boxSizing: 'border-box' }} />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#E67E22' }}>✗ {stageForm.noBranchLabel || 'Nee'} → gaat naar:</label>
                      <select value={stageForm.noBranch} onChange={(e) => setStageForm({...stageForm, noBranch: e.target.value})}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #E67E22', backgroundColor: 'white', boxSizing: 'border-box' }}>
                        <option value="">-- Selecteer bestemming --</option>
                        <option value="__END__">⬤ Direct naar EINDE</option>
                        {(getActiveJourney().stages || []).filter(s => s.id !== editingStageId).map(s => (
                          <option key={s.id} value={s.id}>{s.type === 'end' ? '⬤' : s.type === 'decision' ? '◇' : '📦'} {s.stage}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                
                {stageForm.type !== 'end' && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Primaire Persona</label>
                    <select value={stageForm.primaryPersona} onChange={(e) => setStageForm({...stageForm, primaryPersona: e.target.value})} 
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #BDC3C7', boxSizing: 'border-box', backgroundColor: 'white' }}>
                      <option value="">-- Geen persona --</option>
                      {Object.entries(personas).map(([key, persona]) => (
                        <option key={key} value={key}>{persona.naam} ({persona.organisatie})</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {stageForm.type === 'step' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600' }}>Links</label>
                      <button type="button" onClick={addStageLink} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px dashed #FF8C42', backgroundColor: 'transparent', color: '#FF8C42', cursor: 'pointer', fontSize: '12px' }}>+ Link</button>
                    </div>
                    {stageLinks.map((link, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input value={link.naam} onChange={(e) => updateStageLink(i, 'naam', e.target.value)} placeholder="Naam" style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #BDC3C7' }} />
                        <input value={link.url} onChange={(e) => updateStageLink(i, 'url', e.target.value)} placeholder="URL" style={{ flex: 2, padding: '8px', borderRadius: '6px', border: '1px solid #BDC3C7' }} />
                        <button type="button" onClick={() => removeStageLink(i)} style={{ color: '#E74C3C', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeStageModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingStageId ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Journey Modal */}
      {showJourneyModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={(e) => e.target === e.currentTarget && closeJourneyModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '3px solid #27AE60', backgroundColor: '#E8F8F0', borderRadius: '16px 16px 0 0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50' }}>📋 {editingJourneyId ? 'Procesgang Bewerken' : 'Nieuwe Procesgang'}</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveJourney(); }}>
              <div style={{ padding: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#2C3E50' }}>Naam van de procesgang</label>
                  <input ref={journeyNaamRef} defaultValue={editingJourneyId ? journeys.find(j => j.id === editingJourneyId)?.naam : ''} placeholder="bijv. Onboarding Journey" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #27AE60', boxSizing: 'border-box', fontSize: '14px' }} />
                </div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeJourneyModal} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer', fontWeight: '500' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#27AE60', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingJourneyId ? '💾 Opslaan' : '➕ Aanmaken'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bundle Modal */}
      {showBundleModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={(e) => e.target === e.currentTarget && closeBundleModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '3px solid #FF8C42', backgroundColor: '#FFF5EE' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50' }}>{editingBundleId ? '✏️ Bundel Bewerken' : '➕ Nieuwe Bundel'}</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveBundle(); }}>
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Bundel Naam *</label>
                  <input ref={bundleNaamRef} defaultValue={bundleForm.naam} placeholder="bijv. Intake Fase" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #FF8C42', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Selecteer stappen (min. 2):</label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ECF0F1', borderRadius: '8px', padding: '8px' }}>
                    {(getActiveJourney().stages || []).map((stage, idx) => (
                      <label key={stage.id} style={{ display: 'flex', alignItems: 'center', padding: '8px', borderRadius: '6px', cursor: 'pointer', backgroundColor: bundleForm.stageIds.includes(stage.id) ? '#FFE5D9' : 'transparent' }}>
                        <input type="checkbox" checked={bundleForm.stageIds.includes(stage.id)} onChange={() => toggleStageInBundle(stage.id)} style={{ marginRight: '10px' }} />
                        <span style={{ fontWeight: '600', marginRight: '8px' }}>{idx + 1}.</span>
                        <span>{stage.stage}</span>
                      </label>
                    ))}
                    {(getActiveJourney().stages || []).length === 0 && (
                      <p style={{ textAlign: 'center', color: '#BDC3C7', padding: '16px' }}>Voeg eerst stappen toe aan deze procesgang</p>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#7F8C8D', marginTop: '8px' }}>Geselecteerd: {bundleForm.stageIds.length} stappen</p>
                </div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeBundleModal} style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingBundleId ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Vision Modal */}
      {showVisionModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={(e) => e.target === e.currentTarget && closeVisionModal()}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '3px solid ' + (visionCategory === 'it' ? '#3498DB' : '#FF8C42'), backgroundColor: visionCategory === 'it' ? '#EBF5FB' : '#FFF5EE', borderRadius: '16px 16px 0 0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2C3E50' }}>{visionCategory === 'it' ? '💻' : '🎯'} {editingVisionId ? 'Speerpunten Bewerken' : 'Nieuwe Persoon'} ({visionCategory === 'it' ? 'IT' : 'Business'})</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveVision(); }}>
              <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#2C3E50' }}>Naam</label>
                  <input ref={visionNaamRef} defaultValue={editingVisionId ? (visionCategory === 'it' ? strategicVisions : businessVisions).find(v => v.id === editingVisionId)?.naam : ''} placeholder="bijv. Jeff" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #BDC3C7', boxSizing: 'border-box', fontSize: '14px' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#2C3E50' }}>Icoon</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px', border: '2px solid #BDC3C7', maxHeight: '160px', overflowY: 'auto' }}>
                    {PERSONA_ICONS.map((iconConfig) => {
                      const IconComponent = iconConfig.icon;
                      const isSelected = visionIcon === iconConfig.id;
                      return (
                        <button key={iconConfig.id} type="button" title={iconConfig.name} onClick={() => setVisionIcon(iconConfig.id)}
                          style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                            border: isSelected ? '2px solid ' + (visionCategory === 'it' ? '#3498DB' : '#FF8C42') : '2px solid transparent', backgroundColor: isSelected ? (visionCategory === 'it' ? '#EBF5FB' : '#FFE5D9') : 'white',
                            color: isSelected ? (visionCategory === 'it' ? '#3498DB' : '#FF8C42') : '#7F8C8D', cursor: 'pointer' }}>
                          <IconComponent className="w-5 h-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#2C3E50' }}>Speerpunten / Visie</label>
                  <textarea ref={visionQuoteRef} defaultValue={editingVisionId ? (visionCategory === 'it' ? strategicVisions : businessVisions).find(v => v.id === editingVisionId)?.quote : ''} rows={4} placeholder="bijv. Verbetering intake, stabiliteit, nabijheid van IT organisatie" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #BDC3C7', boxSizing: 'border-box', fontSize: '14px', resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ padding: '12px 20px', borderTop: '1px solid #ECF0F1', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeVisionModal} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#ECF0F1', cursor: 'pointer', fontWeight: '500' }}>Annuleren</button>
                <button type="submit" style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', backgroundColor: visionCategory === 'it' ? '#3498DB' : '#FF8C42', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>{editingVisionId ? '💾 Opslaan' : '➕ Toevoegen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {currentView === 'persona-journey' && <PersonaJourneyView />}
      {currentView === 'arts-dashboard' && <ArtsDashboardView />}
      {currentView === 'arts-mobile' && <ArtsMobileView />}
      {currentView === 'intake-app' && <IntakeAppView />}
	{currentView === 'epics' && <EpicOverview onNavigate={setCurrentView} />}
	{currentView === 'supervisie' && <SupervisieDashboard />}
	{currentView === 'salarisverwerking' && <SalarisverwerkingDashboard />}
    </div>
  );
}
